import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { euro, euroInt, stripParen, extractParen, priceTtcOf, setDisplayCurrency, curSymbol, displayCurrency, depositPct } from "@/lib/format";
import { sendQuotePdfEmail, generatePosterPdf, generateQuotePdf } from "@/lib/pdfLazy";
import { compressImage } from "@/lib/compressImage";
import { PosterDialog } from "@/components/PosterDialog";
import { PosterPreview } from "@/components/PosterPreview";
import { ColorPicker } from "@/components/ColorPicker";
import { StripePicker } from "@/components/StripePicker";
import { defaultStripe, hasStripes, hasArches, singleStripe, joinStripes, splitStripes } from "@/lib/stripes";
import { REPRISE_KEYS, REPRISE_BEST } from "@/components/RepriseBrand";
import { RepriseHtpFields } from "@/components/RepriseHtpFields";
import { useHtpCriteria, buildEstimateUrl, htpToReprise, htpToClient, HTP_ORIGINS } from "@/lib/htp";
import { SignView } from "@/components/SignView";
import { ModelInfo } from "@/components/ModelInfo";
import { OptionInfo } from "@/components/OptionInfo";
import { PlacePicker } from "@/components/PlacePicker";
import { PayMethods } from "@/components/PayMethods";
import { DeliveryPlacePicker } from "@/components/DeliveryPlacePicker";
import { getActAs, setSwitchHook } from "@/lib/actAs";
import { AmountInput, PctInput } from "@/components/AmountInput";
import { useI18n } from "@/context/I18nContext";
import { isPlaceOpt, placeOf, placeLabel } from "@/lib/placeOptions";
import { PHONE_CODES } from "@/lib/phoneCodes";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { t as tr } from "@/lib/i18n";
import { PhoneWarn } from "@/components/PhoneWarn";
import { equipmentOf } from "@/lib/poster";
import { RibDialog } from "@/components/RibDialog";
import { isPromoOn } from "@/lib/promoChoice";
import { CityInput, countryCodeOf } from "@/components/CityInput";
import { ClientTypeField } from "@/components/ClientTypeField";
import { ImportTradeInDialog, tradeInToUsed } from "@/components/ImportTradeInDialog";
import { VehicleSourcePicker, PtacPicker } from "@/components/VehicleSourcePicker";
import { FinancingBlock } from "@/components/FinancingBlock";
import { ensureDraftId, markPending, clearPending, notifySaveFailed, retryPending } from "@/lib/pendingQuote";
import { copySignLink } from "@/lib/copyLink";
import { promoAppliesTo } from "@/lib/promo";

const REG_FEES = [{ key: "cg", label: "Carte grise", ttc: 13.76, vat: 0 }, { key: "plate", label: "Frais de plaque", ttc: 30, vat: 0.2 }, { key: "admin", label: "Frais administratifs", ttc: 86.24, resTtc: 46.24, vat: 0.2 }];
import { UsedVanDialog, UsedVanInline, usedLine, warrantyLine, toUsedModel, USED_DRAFT_ID, usedDraftModel, saveUsed, localizePoster } from "@/components/UsedVanDialog";
import { motion } from "framer-motion";
import { splitAddress } from "@/lib/clientAddress";
import {
  Loader2, MapPin, Mail, Save, Info, Check, ArrowLeft, Eye, EyeOff, Scale, Tag, Plus, Trash2, Paperclip, Sparkles, Printer, Landmark, Pencil, Store, Download, Package, RotateCcw, ExternalLink, ChevronDown, FileText,
} from "lucide-react";

const COLOR_ORDER = ["blanc", "gris perle", "gris souris", "gris anthracite"];
const colorRank = (name, i) => {
  const k = COLOR_ORDER.indexOf((name || "").trim().toLowerCase());
  return k >= 0 ? k - 100 : i;
};


export default function ModelConfigurator() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [searchParams] = useSearchParams();
  const [group, setGroup] = useState(searchParams.get("group"));
  const [vanMenu, setVanMenu] = useState(null);
  const [ranges, setRanges] = useState([]);
  const [rangeKey, setRangeKey] = useState(null);
  const [catalog, setCatalog] = useState({ models: [], options: [] });
  const [countries, setCountries] = useState([]);
  const [countryCode, setCountryCode] = useState(() => localStorage.getItem("fautras_country") || "FR");
  useEffect(() => {
    if (countryCode) setClient((c) => (c.country ? c : { ...c, country: countryCode }));
  }, [countryCode]); // eslint-disable-line react-hooks/exhaustive-deps
  const [modelId, setModelId] = useState(null);
  const expoId = searchParams.get("expo");
  const quoteParam = searchParams.get("quote");
  const replaceParam = searchParams.get("replace") === "1";
  const [replacesId, setReplacesId] = useState("");
  const [expoConfig, setExpoConfig] = useState(null);
  const [cfgReady, setCfgReady] = useState(false);
  const [resetTick, setResetTick] = useState(0);
  const skipCfgRef = useRef(false);
  useEffect(() => { skipCfgRef.current = false; }, [expoConfig]);
  const repricedRef = useRef(false);
  const navigate = useNavigate();
  const [sizeFilter] = useState(() => { try { return JSON.parse(localStorage.getItem("fautras_size")) || { std: true, isl: false }; } catch { return { std: true, isl: false }; } });
  const [selected, setSelected] = useState({}); // id -> {opt, qty}
  const normName = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const INCOMPAT = [
    { range: "van", trigger: "fleche rallongee", block: "coffre a selle", label: "Flèche rallongée" },
    { range: "van", trigger: "coffre a selle", block: "fleche rallongee", label: "Coffre à selle" },
    { trigger: "jantes alu", block: "enjoliveur", label: "Jantes alu" },
    { trigger: "enjoliveur", block: "jantes alu", label: "Enjoliveurs" },
    ...["malle classic", "malle style", "malle premium", "easy lift"].flatMap((m) => [
      { range: "van", trigger: m, block: "coffre a selle", label: m.startsWith("easy") ? "Easy Lift" : "Malle" },
      { range: "van", trigger: "coffre a selle", block: m, label: "Coffre à selle" },
    ]),
    { range: "prosport", trigger: "ouverture pont", block: "arriere auvent", label: "Ouverture pont" },
    { range: "prosport", trigger: "arriere auvent", block: "ouverture pont", label: "Ouverture arrière auvent" },
    { range: "prosport", trigger: "ouverture pont", block: "rampe alu", label: "Ouverture pont" },
    { range: "prosport", trigger: "rampe alu", block: "ouverture pont", label: "Rampe aluminium" },
    { range: "prosport", trigger: "ouverture pont", block: "rampe de chargement", label: "Ouverture pont" },
    { range: "prosport", trigger: "rampe de chargement", block: "ouverture pont", label: "Rampe de chargement" },
  ];
  const [modelAfter, setModelAfter] = useState("");
  const [color, setColor] = useState("");
  const [ralText, setRalText] = useState("");
  const [stripe, setStripe] = useState("");
  const [stripeTouched, setStripeTouched] = useState(false);
  const [stripeBottom, setStripeBottom] = useState("");
  const [arch, setArch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedQuote, setSavedQuote] = useState(null);
  const [offlineSaved, setOfflineSaved] = useState(null);
  const [showReseller, setShowReseller] = useState(false);
  const [tradeIns, setTradeIns] = useState([]);
  const [modelPriceTtc, setModelPriceTtc] = useState("");
  const [optionsAfter, setOptionsAfter] = useState("");
  const [totalAfter, setTotalAfter] = useState("");
  const [acompte, setAcompte] = useState("");
  const [defaultDepositPct, setDefaultDepositPct] = useState(35);
  const [regAmounts, setRegAmounts] = useState({});
  useEffect(() => {
    api.get("/company").then(({ data }) => { if (data?.deposit_pct != null) setDefaultDepositPct(Number(data.deposit_pct)); setRegAmounts(data || {}); }).catch(() => {});
  }, []);
  const [depositPay, setDepositPay] = useState([]);
  const [balancePay, setBalancePay] = useState([]);
  const [financing, setFinancing] = useState(null);
  const [usedDlg, setUsedDlg] = useState(null);
  const [draftSeed, setDraftSeed] = useState({ key: 0, used: {} });
  const [usedExpo, setUsedExpo] = useState(null);
  const [usedBusy, setUsedBusy] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const importTradeIn = (used) => {
    setDraftSeed({ key: Date.now(), used });
    setCatalog((c) => ({ ...c, models: c.models.map((m) => (m.id === USED_DRAFT_ID ? usedDraftModel(used) : m)) }));
    setModelId(USED_DRAFT_ID);
    toast.success("Reprise importée : complétez le prix et le descriptif");
  };
  const finOffsetRef = useRef({ acompte: 0, apport: 0, net: 0 });
  useEffect(() => {
    const host = new URL(process.env.REACT_APP_FINANCEMENT_URL).host.replace(/^www\./, "");
    const origins = [`https://${host}`, `https://www.${host}`];
    const onMsg = (e) => {
      const d = e.data || {};
      if (!origins.includes(e.origin) || d.type !== "fautras-financement" || !(Number(d.mensualite) > 0)) return;
      const lease = String(d.provider || "").toLowerCase() === "mutualease";
      setFinancing({ solution: String(d.solution || ""), provider: String(d.provider || ""), duree: Number(d.duree) || 0, mensualite: Number(d.mensualite),
        taeg: d.taeg != null ? Number(d.taeg) : null, montant: Number(d.montant) || 0, cout_total: d.cout_total != null ? Number(d.cout_total) : null,
        premier_versement: Number(d.premier_versement) > 0 ? Number(d.premier_versement) : null, prix_ttc: Number(d.prix_ttc) || null });
      setBalancePay((xs) => (xs.includes("financement") ? xs : [...xs, "financement"]));
      const hasApport = !lease && d.apport != null && d.apport !== "" && Number(d.apport) >= 0;
      // L'apport du simulateur inclut la reprise : seul l'écart d'apport modifie l'acompte
      const f = finOffsetRef.current;
      const dep = Math.min(f.net, Math.max(0, Math.round((f.acompte + Number(d.apport) - f.apport) * 100) / 100));
      if (hasApport) setAcompte(String(dep));
      toast.success(hasApport ? `Financement ajouté au devis · acompte : ${Math.round(dep)} €` : "Financement ajouté au devis");
    };
    window.addEventListener("message", onMsg);
    return () => { window.removeEventListener("message", onMsg); };
  }, []);
  const [promos, setPromos] = useState([]);
  const [promosFor, setPromosFor] = useState(null);
  const [activePromoIds, setActivePromoIds] = useState(() => new Set());
  const [signature, setSignature] = useState("");
  const [cgvAccepted, setCgvAccepted] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  const [signQuote, setSignQuote] = useState(null);
  const makeSpecial = () => ({ id: `sp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, name: "", priceTtc: "", disc: "", description: "", attachmentUrl: "", attachmentName: "", uploading: false });
  const spDisc = (s) => Math.max(0, Math.min(100, Number(s.disc) || 0));
  const spNet = (s) => (Number(s.priceTtc) || 0) * (1 - spDisc(s) / 100);
  const [specials, setSpecials] = useState(() => [makeSpecial()]);
  const addSpecial = () => setSpecials((xs) => [...xs, makeSpecial()]);
  const specialName = (d) => { const w = (d || "").replace(/\s+/g, " ").trim().split(" ").slice(0, 6).join(" "); return w.length > 60 ? `${w.slice(0, 57).trim()}…` : w; };
  const updateSpecial = (id, patch) => setSpecials((xs) => xs.map((x) => (x.id === id ? { ...x, ...patch, ...("description" in patch ? { name: specialName(patch.description) } : {}) } : x)));
  const removeSpecial = (id) => setSpecials((xs) => xs.filter((x) => x.id !== id));
  const uploadReprisePhotos = async (i, files) => {
    const list = Array.from(files || []).filter((f) => f.type.startsWith("image/"));
    if (!list.length) return;
    const t = toast.loading("Envoi des photos…");
    try {
      const added = [];
      for (const f of list) {
        const fd = new FormData();
        fd.append("file", await compressImage(f));
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        added.push({ url: data.url, name: f.name });
      }
      setTradeIns((xs) => xs.map((x, idx) => (idx === i ? { ...x, photos: [...(x.photos || []), ...added] } : x)));
      toast.success(`${added.length} photo(s) ajoutée(s)`, { id: t });
    } catch (e) {
      toast.error(tr("Échec de l'envoi des photos"), { id: t });
    }
  };
  const removeReprisePhoto = (i, url) => setTradeIns((xs) => xs.map((x, idx) => (idx === i ? { ...x, photos: (x.photos || []).filter((p) => p.url !== url) } : x)));

  const uploadSpecial = async (id, file) => {
    if (!file) return;
    updateSpecial(id, { uploading: true });
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      updateSpecial(id, { attachmentUrl: data.url, attachmentName: file.name, uploading: false });
      toast.success(tr("Pièce jointe ajoutée"));
    } catch (e) {
      toast.error(tr("Échec de l'envoi du fichier"));
      updateSpecial(id, { uploading: false });
    }
  };

  // quote form modal (comme Fautras "Recevoir le devis")
  const [formOpen, setFormOpen] = useState(false);
  const [missing, setMissing] = useState([]);
  const [posterOpen, setPosterOpen] = useState(false);
  const [posterPdf, setPosterPdf] = useState(null);
  const [orderPdf, setOrderPdf] = useState(null);
  const [client, setClient] = useState(() => {
    try { return { name: "", firstname: "", company: "", email: "", phone: "", phone_code: "+33", postal: "", city: "", country: "", address: "", ...(JSON.parse(localStorage.getItem("fautras_client")) || {}) }; }
    catch { return { name: "", firstname: "", company: "", email: "", phone: "", phone_code: "+33", postal: "", city: "", country: "", address: "" }; }
  });
  useEffect(() => { localStorage.setItem("fautras_client", JSON.stringify(client)); }, [client]);
  const [deliveryPlace, setDeliveryPlace] = useState("");
  const [deliveryDelay, setDeliveryDelay] = useState("");
  const [delayImperative, setDelayImperative] = useState(false);
  const [vehicleSource, setVehicleSource] = useState("");
  const [regSel, setRegSel] = useState(null);
  const [regDiscount, setRegDiscount] = useState("");
  const [regOpen, setRegOpen] = useState(false);
  const [htOnly, setHtOnly] = useState(false);
  const [stockLocation, setStockLocation] = useState("");
  const [serialNumber, setSerialNumber] = useState(null);
  const [ptac, setPtac] = useState(null);
  const expoSerial = expoConfig && !expoConfig._quote ? expoConfig.serial_number || "" : null;
  const [deliveryTouched, setDeliveryTouched] = useState(false);
  const [deliveryHint, setDeliveryHint] = useState("");
  const [ownPlace, setOwnPlace] = useState("");
  useEffect(() => {
    api.get("/events").then(({ data }) => {
      const own = (data || []).find((p) => p.kind === "stock" && p.own);
      setOwnPlace(own ? (own.company ? `${t("Chez")} ${own.company} (${own.city})` : own.city) : "");
    }).catch(() => {});
  }, [t]);
  useEffect(() => {
    if (deliveryTouched || !rangeKey || !countryCode) return;
    if (!(client.postal || "").trim()) { setDeliveryHint(""); setDeliveryPlace(ownPlace); return; }
    api.get("/sector-reseller", { params: { range_key: rangeKey, country: countryCode, postal: client.postal || "" } })
      .then(({ data }) => {
        setDeliveryHint(data?.company ? `${data.name || ""} (${data.company})` : "");
        setDeliveryPlace(data?.company && data?.city ? `${t("Chez")} ${data.host || data.company} (${data.city})` : ownPlace);
      })
      .catch(() => {});
  }, [rangeKey, countryCode, client.postal, deliveryTouched, ownPlace, t]);
  const [horizon, setHorizon] = useState("immediate");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    const paramRange = searchParams.get("range");
    const loadRanges = (expo) => api.get("/ranges").then(({ data }) => {
      setRanges(data);
      const initial = data.find((r) => r.key === (expo?.range_key || paramRange)) || data[0];
      if (initial) setRangeKey(initial.key);
    }).finally(() => setLoading(false));
    if (expoId) {
      api.get(`/expos/${expoId}`).then(({ data }) => { if (data.country_code) setCountryCode(data.country_code); setExpoConfig(data); return loadRanges(data); })
        .catch(() => { toast.error("Van en stock introuvable ou non autorisé"); return loadRanges(null); });
    } else if (quoteParam) {
      api.get(`/quotes/${quoteParam}`).then(({ data }) => {
        const opts = {};
        (data.options || []).forEach((o) => { opts[o.id] = { id: o.id, qty: (opts[o.id]?.qty || 0) + (o.qty || 1), disc: o.discount_pct || 0, broches: o.broches, place: o.place }; });
        const cfg = { range_key: data.range_key, model_id: data.model_id, options: Object.values(opts),
          promo_names: (data.promo_name || "").split(", ").filter(Boolean), _quote: data };
        const c = data.client || {};
        if (data.client_id) localStorage.setItem("fautras_client_id", data.client_id);
        if (data.country_code && data.range_key !== "occasion") setCountryCode(data.country_code);
        setClient((prev) => ({ ...prev, name: c.name || "", firstname: c.firstname || "", company: c.company || "", client_type: c.client_type || "", siret: c.siret || "", email: c.email || "", phone: (c.phone || "").replace(/^\s*\+\d{1,4}\s*/, ""), phone_code: ((c.phone || "").match(/^\s*(\+\d{1,4})/) || [])[1] || "", ...splitAddress(c) }));
        setReplacesId(data.signature && replaceParam && !data.cancelled ? data.id : "");
        if (data.signature && replaceParam && !data.cancelled) {
          localStorage.removeItem("fautras_draft_id");
          toast.info(`${tr("Modification de la commande")} ${data.quote_number} : ${tr("la nouvelle commande portera la mention « annule et remplace » et devra être signée par le client. L'ancienne est suspendue jusqu'à cette signature.")}`, { duration: 9000, id: `replace-${data.id}` });
        } else if (data.signature) { localStorage.removeItem("fautras_draft_id"); toast.info(`Commande ${data.quote_number} signée : elle ne sera pas modifiée. Toute modification créera un nouveau devis à faire signer.`, { duration: 8000 }); } else localStorage.setItem("fautras_draft_id", data.id);
        setExpoConfig(cfg);
        return loadRanges(cfg);
      }).catch(() => { toast.error("Devis introuvable ou non autorisé"); return loadRanges(null); });
    } else { setExpoConfig(null); loadRanges(null); }
    api.get("/countries").then(({ data }) => setCountries(data)).catch(() => {});
  }, [searchParams, expoId, quoteParam]);

  const lastRangeRef = useRef(null);
  const lastUsedKeyRef = useRef(null);
  useEffect(() => {
    if (!rangeKey) return;
    const sameRange = lastRangeRef.current === rangeKey;
    lastRangeRef.current = rangeKey;
    const usedKey = `${searchParams.toString()}|${expoConfig?._quote?.id || expoConfig?.id || ""}`;
    if (rangeKey === "occasion" && sameRange && lastUsedKeyRef.current === usedKey) return;
    lastUsedKeyRef.current = rangeKey === "occasion" ? usedKey : null;
    if (!sameRange) { setCatalog({ models: [], options: [] }); setSelected({}); }
    const modelParam = expoConfig?.model_id || searchParams.get("model");
    api.get(`/catalog/${rangeKey}`, { params: rangeKey !== "occasion" ? { country: countryCode } : {} }).then(({ data: raw }) => {
      let data = raw;
      if (rangeKey === "occasion") {
        const q = expoConfig?._quote;
        const fromQuote = q?.range_key === "occasion" && !raw.models.some((m) => m.id === q.model_id) ? (q.used || {}) : null;
        if (fromQuote) setDraftSeed({ key: Date.now(), used: fromQuote });
        data = { ...raw, models: [...raw.models, usedDraftModel(fromQuote || {})] };
        if (fromQuote || !modelParam) data._preferred = USED_DRAFT_ID;
        const rep = searchParams.get("reprise");
        if (rep && !fromQuote) {
          const [qid, idx] = rep.split(":");
          api.get(`/quotes/${qid}`).then(({ data: rq }) => { const t = (rq.trade_ins || [])[Number(idx)]; if (t) importTradeIn(tradeInToUsed(t, { quote_id: qid, index: Number(idx) })); }).catch(() => toast.error("Reprise introuvable"));
        }
      }
      setCatalog(data);
      setPricing(data.pricing || null);
      if (data.pricing && data.pricing.country !== countryCode) setCountryCode(data.pricing.country);
      const preferred = (data._preferred && data.models.find((m) => m.id === data._preferred)) || (modelParam && data.models.find((m) => m.id === modelParam));
      if (sameRange) {
        setSelected((prev) => Object.fromEntries(Object.entries(prev).map(([k, e]) => [k, { ...e, opt: data.options.find((o) => o.id === k) || e.opt }])));
        setModelId((cur) => cur || (preferred || data.models[0])?.id || null);
      } else setModelId((preferred || data.models[0])?.id || null);
    });
  }, [rangeKey, group, searchParams, expoConfig, countryCode]); // eslint-disable-line react-hooks/exhaustive-deps
  const [pricing, setPricing] = useState(null);
  const [showEur, setShowEur] = useState(() => localStorage.getItem("fautras_show_eur") === "true");
  const eurView = showEur && pricing && pricing.currency !== "EUR";
  setDisplayCurrency(eurView ? "EUR" : pricing?.currency, eurView ? 1 : pricing?.rate);
  const sym = curSymbol();
  useEffect(() => () => setDisplayCurrency(), []);

  const [availOptions, setAvailOptions] = useState([]);
  const optCountryRef = useRef(countryCode);
  useEffect(() => {
    if (optCountryRef.current === countryCode) return;
    optCountryRef.current = countryCode;
    if (rangeKey !== "van" || !modelId) return;
    api.get(`/available-options/${rangeKey}/${modelId}`, { params: { country: countryCode } }).then(({ data }) => {
      setAvailOptions(data);
      setSelected((prev) => Object.fromEntries(Object.entries(prev).map(([k, e]) => [k, { ...e, opt: data.find((o) => o.id === k) || e.opt }])));
    }).catch(() => {});
  }, [countryCode]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!rangeKey || !modelId) { setAvailOptions([]); return; }
    setSelected({});
    setTradeIns([]);
    setModelAfter("");
    setOptionsAfter("");
    setTotalAfter("");
    setAcompte("");
    setSpecials([makeSpecial()]);
    (modelId === USED_DRAFT_ID ? Promise.resolve({ data: [] }) : api.get(`/available-options/${rangeKey}/${modelId}`, { params: rangeKey === "van" ? { country: countryCode } : {} })).then(({ data }) => {
      setAvailOptions(data);
      const pre = {};
      const loadingCfg = !skipCfgRef.current && expoConfig && (expoConfig.model_id === modelId || (expoConfig.range_key === "occasion" && !expoConfig.model_id && modelId === USED_DRAFT_ID));
      for (const o of (loadingCfg ? [] : data)) {
        if ((o.preselected_models || []).includes(modelId) && (o.public_ht || 0) > 0) {
          pre[o.id] = { opt: o, qty: 1, disc: 0 };
        }
      }
      const curModel = catalog.models.find((m) => m.id === modelId);
      if (!loadingCfg && curModel && (curModel.category || "").toUpperCase().includes("DROIT")) {
        const coffre = data.find((o) => /coffre/i.test(o.name) && /selle/i.test(o.name) && (o.public_ht || 0) > 0);
        if (coffre && !pre[coffre.id]) pre[coffre.id] = { opt: coffre, qty: 1, disc: 0 };
      }
      if (loadingCfg) {
        for (const eo of (expoConfig.options || [])) {
          const o = data.find((x) => x.id === eo.id);
          if (o) pre[o.id] = { opt: o, qty: eo.qty || 1, disc: eo.disc || 0, priceTtc: eo.price_ttc ?? undefined, broches: eo.broches, place: placeOf(o, eo.qty || 1, eo.place) };
        }
        const pids = (expoConfig.promo_ids && expoConfig.promo_ids.length) ? expoConfig.promo_ids : (expoConfig.promo_id ? [expoConfig.promo_id] : []);
        if (pids.length) setActivePromoIds(new Set(pids));
        const q = expoConfig._quote;
        const sp = ((q ? q.special_options : expoConfig.special_options) || []).map((s) => ({ ...makeSpecial(), name: s.name || "", priceTtc: s.price_ttc || "", disc: s.discount_pct || "", description: s.description || "", attachmentUrl: s.attachment_url || "", attachmentName: s.attachment_name || "" }));
        if (sp.length) setSpecials(sp);
        if (!q) {
          const str = (v) => (v == null ? "" : String(v));
          setModelAfter(str(expoConfig.model_after)); setOptionsAfter(str(expoConfig.options_after)); setTotalAfter(str(expoConfig.total_after));
        }
        if (q) {
          if ((q.discount_amount || 0) > 0 && q.total_public_ttc != null) setTotalAfter(String(q.total_public_ttc));
          else if (q.model_public_ttc != null && curModel && Math.abs(q.model_public_ttc - priceTtcOf(curModel, q.vat_rate ?? 0.2)) > 0.5) setModelAfter(String(q.model_public_ttc));
          if ((q.trade_ins || []).length) setTradeIns(q.trade_ins.map((t) => ({ ...makeReprise(), ...t, montant: t.montant_ttc || 0, tvaRecup: !!t.tva_recuperable })));
          if (q.delivery_place) { setDeliveryTouched(true); setDeliveryPlace(q.delivery_place); }
          setDeliveryDelay(q.delivery_delay || ""); setDelayImperative(!!q.delay_imperative);
          setVehicleSource(q.vehicle_source || ""); setRegSel(Array.isArray(q.reg_fees) ? q.reg_fees : []); setRegDiscount(q.reg_discount ? String(q.reg_discount) : ""); setStockLocation(q.stock_location || ""); setSerialNumber(q.serial_number || ""); setPtac(q.ptac ?? null);
          setNotes(q.notes || "");
          if (q.deposit != null && Math.abs(q.deposit - Math.round(defaultDepositPct / 100 * (q.total_public_ttc || 0))) > 1) setAcompte(String(q.deposit));
          setDepositPay(q.deposit_methods || []); setBalancePay(q.balance_methods || []); setHtOnly(!!q.ht_only);
          setFinancing(q.financing && q.financing.mensualite ? q.financing : null);
        }
      }
      const savedColor = loadingCfg ? ((expoConfig._quote ? expoConfig._quote.color : expoConfig.color) || "") : "";
      if (loadingCfg && !expoConfig._quote && expoConfig.ptac) setPtac(expoConfig.ptac);
      const pal = curModel?.colors || [];
      const [savedBase, ...savedRest] = savedColor.split(" : ");
      setRalText(savedRest.join(" : "));
      const onlyStd = pal.filter((c) => c.type !== "special" && !/ral/i.test(c.name));
      setColor(savedBase && pal.some((c) => c.name === savedBase) ? savedBase : (onlyStd.length === 1 ? onlyStd[0].name : ""));
      const savedStripe = loadingCfg ? ((expoConfig._quote ? expoConfig._quote.stripe_color : expoConfig.stripe_color) || "") : "";
      const [sTop, sBottom] = splitStripes(savedStripe);
      setStripe(sTop); setStripeTouched(!!sTop); setStripeBottom(sBottom);
      const savedArch = loadingCfg ? ((expoConfig._quote ? expoConfig._quote.arch_color : expoConfig.arch_color) || "") : "";
      setArch(savedArch && savedArch !== (sTop || defaultStripe(savedColor)) ? savedArch : "");
      setSelected(pre);
      if (loadingCfg) setCfgReady(true);
    }).catch(() => setAvailOptions([]));
  }, [rangeKey, modelId, expoConfig, resetTick]);

  useEffect(() => {
    const refetch = () => {
      if (document.visibilityState === "visible" && rangeKey && modelId && modelId !== USED_DRAFT_ID) {
        api.get(`/available-options/${rangeKey}/${modelId}`).then(({ data }) => setAvailOptions(data)).catch(() => {});
      }
    };
    window.addEventListener("focus", refetch);
    document.addEventListener("visibilitychange", refetch);
    return () => { window.removeEventListener("focus", refetch); document.removeEventListener("visibilitychange", refetch); };
  }, [rangeKey, modelId]);

  useEffect(() => {
    const onWheel = () => {
      const el = document.activeElement;
      if (el && el.tagName === "INPUT" && el.type === "number") el.blur();
    };
    document.addEventListener("wheel", onWheel, { passive: true });
    return () => document.removeEventListener("wheel", onWheel);
  }, []);

  const currentRange = ranges.find((r) => r.key === rangeKey);
  const model = catalog.models.find((m) => m.id === modelId);
  useEffect(() => {
    if (rangeKey === "van" && !vanMenu) api.get("/menu/van").then(({ data }) => setVanMenu(data)).catch(() => {});
  }, [rangeKey, vanMenu]);
  const modelGroup = useMemo(() => (vanMenu?.groups || []).find((g) => g.entries.some((e) => e.model?.id === modelId || (e.versions || []).some((v) => v.model?.id === modelId)))?.key, [vanMenu, modelId]);
  useEffect(() => {
    setUsedExpo(null);
    if (rangeKey !== "occasion" || !modelId || modelId === USED_DRAFT_ID) return;
    api.get(`/expos/${modelId}`).then(({ data }) => setUsedExpo(data)).catch(() => {});
  }, [rangeKey, modelId]);
  const patchUsedModel = (u) => setCatalog((c) => ({ ...c, models: c.models.map((m) => (m.id === modelId ? toUsedModel(m, u) : m)) }));
  const reloadUsed = (id) => api.get("/catalog/occasion").then(({ data }) => { setCatalog({ ...data, models: [...data.models, usedDraftModel()] }); setModelId(id); });
  const stockUsed = async (salon, resellerIds, phone) => {
    if (!model || (modelId !== USED_DRAFT_ID && !usedExpo)) return;
    setUsedBusy(true);
    const saved = await saveUsed({ ...(model.used || {}), location: salon, phone }, modelId === USED_DRAFT_ID ? null : usedExpo, resellerIds);
    if (saved) {
      setPosterOpen(false);
      if (Number(saved.used?.price_ttc) > 0) await generatePosterPdf(localizePoster(saved.poster, saved.used));
      else toast.info(t("Occasion en stock sans prix : ajoutez le prix de vente pour imprimer l'affiche."));
      if (modelId === USED_DRAFT_ID) { setDraftSeed({ key: Date.now(), used: {} }); await reloadUsed(saved.id); } else setUsedExpo(saved);
    }
    setUsedBusy(false);
  };

  const [compareOpen, setCompareOpen] = useState(false);
  const [compareId, setCompareId] = useState("");
  const [compareAvail, setCompareAvail] = useState(null);

  const [compareExtras, setCompareExtras] = useState([]);

  // Comparaison par défaut selon le modèle courant : modèle cible + options à ajouter au modèle comparé
  const compareDefault = useMemo(() => {
    const none = { id: "", extras: [] };
    if (!model) return none;
    const n = model.name.trim().toUpperCase();
    const findBase = (prefix) => catalog.models.find((x) => {
      const u = x.name.toUpperCase();
      return u.startsWith(prefix) && !/SELECT|CLASSIC|PREMIUM/.test(u);
    })?.id || "";
    const findExact = (name) => catalog.models.find((x) => x.name.trim().toUpperCase() === name)?.id || "";
    const findClassic = (base) => catalog.models.find((x) => x.name.trim().toUpperCase().startsWith(base + " CLASSIC"))?.id || "";
    const coffreId = catalog.options.find((o) => /coffre/i.test(o.name) && /selle/i.test(o.name))?.id;
    const coffre = (coffreId && n.includes("COFFRE")) ? [coffreId] : [];
    const premiumExtras = () => {
      const aid = catalog.options.find((o) => /aid system/i.test(o.name) && /provan/i.test(o.name))?.id;
      const sortie = catalog.options.find((o) => /sortie avant/i.test(o.name))?.id;
      const enj = catalog.options.find((o) => /enjoliveur/i.test(o.name))?.id;
      return [aid, sortie, enj].filter(Boolean);
    };
    if (n.includes("PROVAN PREMIUM")) return { id: findClassic("PROVAN"), extras: premiumExtras() };
    if (n.includes("PROMAX PREMIUM")) return { id: findClassic("PROMAX"), extras: premiumExtras() };
    if (n.includes("PROVAN SELECT")) return { id: findBase("PROVAN "), extras: coffre };
    if (n.includes("JMS SELECT")) return { id: findBase("JMS "), extras: coffre };
    if (n === "OBLIC +2") return { id: findExact("OBLIC X2"), extras: [] };
    if (n === "OBLIC +3") return { id: findExact("OBLIC X3"), extras: [] };
    return none;
  }, [model, catalog.models, catalog.options]);

  useEffect(() => {
    if (!compareOpen || !compareId || !rangeKey) { setCompareAvail(null); return; }
    api.get(`/available-options/${rangeKey}/${compareId}`)
      .then(({ data }) => setCompareAvail(new Set(data.map((o) => o.id))))
      .catch(() => setCompareAvail(null));
  }, [compareOpen, compareId, rangeKey]);

  const isIsl = (s) => { const n = normName(s); return n.includes("island") || n.includes("icelandic"); };
  const visibleModels = catalog.models.filter((m) => (isIsl(m.name) ? sizeFilter.isl : sizeFilter.std));

  const optionsByCat = useMemo(() => {
    const groups = {};
    for (const o of availOptions) {
      const c = o.category || "Divers";
      (groups[c] = groups[c] || []).push(o);
    }
    return groups;
  }, [availOptions]);
  const ptacOpt = Object.values(selected).find((e) => e.qty > 0 && e.opt?.ptac_values?.length);
  const ptacSrc = ptacOpt ? ptacOpt.opt : model;
  const ptacList = ptacSrc?.ptac_values || [];
  const ptacDef = ptacList.includes(Number(ptacSrc?.ptac_default)) ? Number(ptacSrc.ptac_default) : (ptacList.length ? Math.max(...ptacList) : null);
  const ptacEff = ptacList.includes(Number(ptac)) ? Number(ptac) : ptacDef;
  const ptacCat = "CAPACITE";
  const f2Range = useMemo(() => {
    const vs = [...(model?.ptac_values || []), ...availOptions.flatMap((o) => o.ptac_values || [])].map(Number).filter((v) => v > 0);
    return vs.length ? [Math.min(...vs), Math.max(...vs)] : [];
  }, [model, availOptions]);
  const catsForUi = ptacList.length && !optionsByCat.CAPACITE ? { CAPACITE: [], ...optionsByCat } : optionsByCat;

  const activeTriggers = Object.values(selected).filter((e) => e.qty > 0).map((e) => normName(e.opt.name));
  const blockedOf = (o) => {
    const nm = normName(o.name);
    const r = INCOMPAT.filter((x) => !x.range || x.range === rangeKey).find((x) => nm.includes(x.block) && activeTriggers.some((t) => t.includes(x.trigger)));
    return r ? r.label : null;
  };

  useEffect(() => {
    if (!rangeKey || !countryCode) { setPromos([]); setActivePromoIds(new Set()); return; }
    api.get(`/promotions/active`, { params: { country: countryCode, range_key: rangeKey, all_countries: user?.role === "admin" } })
      .then(({ data }) => {
        const list = data || [];
        const local = (p) => (p.country_codes || []).some((c) => c.toUpperCase() === String(countryCode).toUpperCase());
        setPromos(list);
        const ex = !skipCfgRef.current && expoConfig && !expoConfig._quote && expoConfig.range_key === rangeKey ? ((expoConfig.promo_ids && expoConfig.promo_ids.length) ? expoConfig.promo_ids : (expoConfig.promo_id ? [expoConfig.promo_id] : [])) : null;
        if (ex) setActivePromoIds(new Set(ex.filter((id) => list.some((p) => p.id === id))));
        else setActivePromoIds(new Set(list.filter((p) => local(p) && isPromoOn(p)).map((p) => p.id)));
        setPromosFor(countryCode);
      })
      .catch(() => { setPromos([]); setActivePromoIds(new Set()); });
  }, [rangeKey, countryCode, user?.role, resetTick]); // eslint-disable-line react-hooks/exhaustive-deps
  const resetQuote = () => {
    if (!window.confirm(tr("Réinitialiser les prix du devis ? Les options choisies sont conservées ; les remises et prix modifiés sont supprimés et les prix initiaux (avec promotions salon) sont rétablis."))) return;
    const local = (p) => (p.country_codes || []).some((c) => c.toUpperCase() === String(countryCode).toUpperCase());
    setActivePromoIds(new Set(promos.filter((p) => local(p) && isPromoOn(p)).map((p) => p.id)));
    setSelected((s) => Object.fromEntries(Object.entries(s).map(([id, v]) => [id, { ...v, disc: 0, priceTtc: undefined }])));
    setSpecials((list) => list.map((s) => ({ ...s, disc: "" })));
    setModelPriceTtc(""); setModelAfter(""); setOptionsAfter(""); setTotalAfter(""); setFinalPct(null); setRegDiscount("");
    toast.success(tr("Prix réinitialisés"));
  };

  const homeCountries = useMemo(() => {
    if (!user || user.role === "admin" || user.sales_manager) return null;
    const cs = [...new Set((user.sectors || []).map((s) => s.country).filter(Boolean))];
    return [...new Set([...(cs.length ? cs : ["FR"]), ...(user.extra_price_countries || [])])];
  }, [user]);
  const vatCountry = !homeCountries || homeCountries.includes(countryCode) ? countryCode : homeCountries[0];
  const vatFor = (code) => countries.find((c) => c.code === (!homeCountries || homeCountries.includes(code) ? code : homeCountries[0]))?.vat_rate ?? 0.2;
  const modelPromos = useMemo(() => (modelId ? promos.filter((p) => promoAppliesTo(p, modelId)) : promos), [promos, modelId]);
  const promoModelPct = (m) => {
    if (!m) return { pub: 0, res: 0 };
    let pub = 0, res = 0;
    for (const p of modelPromos) {
      if (!activePromoIds.has(p.id)) continue;
      let mr = (p.model_rules || {})[m.id];
      if (!mr && !(p.no_option_promo_models || []).includes(m.id) && ((p.model_amount_pub_ttc || 0) > 0 || (p.model_amount_res_ht || 0) > 0)) {
        const vat = countries.find((c) => c.code === vatCountry)?.vat_rate ?? 0.2;
        const cat = priceTtcOf(m, vat);
        mr = { pub: cat > 0 ? Math.min(100, (p.model_amount_pub_ttc || 0) / cat * 100) : 0,
          res: m.reseller_ht > 0 ? Math.min(100, (p.model_amount_res_ht || 0) / m.reseller_ht * 100) : 0 };
      }
      if (mr && (mr.pub || 0) > pub) { pub = mr.pub || 0; res = mr.res || 0; }
    }
    return { pub, res };
  };
  const promoMaps = useMemo(() => {
    const { pub: modelPub, res: modelRes } = promoModelPct(model);
    const optPub = {}, optRes = {};
    for (const p of modelPromos) {
      if (!activePromoIds.has(p.id)) continue;
      if ((p.no_option_promo_models || []).includes(modelId)) continue;
      for (const [oid, r] of Object.entries(p.option_rules || {})) {
        if ((r.pub || 0) > (optPub[oid] || 0)) { optPub[oid] = r.pub || 0; optRes[oid] = r.res || 0; }
      }
    }
    return { modelPub, modelRes, optPub, optRes };
  }, [modelPromos, activePromoIds, modelId, model, countries, vatCountry]); // eslint-disable-line react-hooks/exhaustive-deps
  const aidMode = modelPromos.some((p) => p.is_aid && activePromoIds.has(p.id));
  const remWord = aidMode ? "aide" : "remise";
  const [finalPct, setFinalPct] = useState(null);
  const [totalDraft, setTotalDraft] = useState(null);
  const pctBaseRef = useRef(null);
  // Une baisse du total est retirée d'abord des options après remise, puis du modèle s'il n'y a pas assez d'options.
  const applyTotal = (target, base = { mod: totals.modelAfterTtc, opt: totals.optionsAfterTtc }) => {
    let opt = base.opt + (Math.max(0, target) - (base.mod + base.opt));
    let mod = base.mod;
    if (opt < 0) { mod = Math.max(0, mod + opt); opt = 0; }
    setOptionsAfter(String(Math.round(opt * 100) / 100));
    setModelAfter(String(Math.round(mod * 100) / 100));
    setTotalAfter("");
  };

  const totals = useMemo(() => {
    const entries = Object.values(selected);
    const ctry = countries.find((c) => c.code === vatCountry);
    const vat = rangeKey === "occasion" ? (!(model?.vat_rate > 0) ? 0 : homeCountries && ctry ? ctry.vat_rate : model.vat_rate) : (ctry ? ctry.vat_rate : 0.2);
    const mPub = promoMaps.modelPub || 0, mRes = promoMaps.modelRes || 0;
    const catModelTtc = priceTtcOf(model, vat);
    const promoModelTtc = catModelTtc * (1 - mPub / 100);
    const specialsGross = specials.reduce((s, x) => s + (Number(x.priceTtc) || 0), 0);
    const specialsTtc = specials.reduce((s, x) => s + spNet(x), 0);
    const specialsHt = specialsTtc / (1 + vat);
    const catOptTtc = entries.reduce((s, e) => s + priceTtcOf(e.opt, vat) * e.qty, 0) + specialsGross;
    const subBeforeTtc = catModelTtc + catOptTtc;
    const optEffTtc = entries.reduce((s, e) => {
      const cat = priceTtcOf(e.opt, vat);
      const effDisc = (e.disc || 0) > 0 ? e.disc : (promoMaps.optPub[e.opt.id] || 0);
      const unit = (e.priceTtc != null && e.priceTtc !== "") ? Math.max(0, Number(e.priceTtc)) : cat * (1 - effDisc / 100);
      return s + unit * e.qty;
    }, 0) + specialsTtc;
    const modelAfterTtc = (modelAfter !== "" && modelAfter != null) ? Math.max(0, Number(modelAfter)) : promoModelTtc;
    const optionsAfterTtc = (optionsAfter !== "" && optionsAfter != null) ? Math.max(0, Number(optionsAfter)) : optEffTtc;
    const totalAfterTtc = (totalAfter !== "" && totalAfter != null) ? Math.max(0, Number(totalAfter)) : (modelAfterTtc + optionsAfterTtc);
    const clientDiscount = subBeforeTtc - totalAfterTtc;
    const totPubHt = totalAfterTtc / (1 + vat);
    const tva = totalAfterTtc - totPubHt;
    const subResHt = (model?.reseller_ht || 0) * (1 - mRes / 100) + entries.reduce((s, e) => s + (e.opt.reseller_ht || 0) * e.qty * (1 - (promoMaps.optRes[e.opt.id] || 0) / 100), 0) + specialsHt;
    return { vat, catModelTtc, promoModelTtc, catOptTtc, subBeforeTtc, optEffTtc, modelAfterTtc, optionsAfterTtc, totalAfterTtc, clientDiscount, totPubHt, tva, totTtc: totalAfterTtc, subResHt, margin: totPubHt - subResHt };
  }, [model, selected, countries, vatCountry, homeCountries, modelAfter, optionsAfter, totalAfter, promoMaps, specials, rangeKey]);

  const setQty = (opt, qty) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (qty <= 0) delete next[opt.id];
      else {
        const b = Math.min(prev[opt.id]?.place?.b || 0, qty);
        next[opt.id] = { ...prev[opt.id], opt, qty, disc: prev[opt.id]?.disc || 0, priceTtc: prev[opt.id]?.priceTtc, place: isPlaceOpt(opt) ? { a: qty - b, b } : undefined };
      }
      const triggers = qty > 0 ? [normName(opt.name)] : [];
      const rules = INCOMPAT.filter((r) => !r.range || r.range === rangeKey);
      for (const id of Object.keys(next)) {
        if (id === opt.id) continue;
        const nm = normName(next[id].opt.name);
        if (rules.some((r) => nm.includes(r.block) && triggers.some((t) => t.includes(r.trigger)))) delete next[id];
      }
      return next;
    });
  };
  const COLOR_OPT_RE = /^(option couleur speciale|autre ral)/;
  const colorOptFor = (c) => {
    if (c?.type !== "special") return null;
    const find = (p) => availOptions.find((o) => normName(o.name).startsWith(p));
    return (/ral/i.test(c.name) && find("autre ral")) || find("option couleur speciale");
  };
  const colorLabel = /ral/i.test(color) && ralText.trim() ? `${color} : ${ralText.trim()}` : color;
  const withStripes = rangeKey !== "occasion" && hasStripes(model);
  const stripeTop = withStripes ? (stripeTouched && stripe ? stripe : defaultStripe(color)) : "";
  const stripeLabel = withStripes ? joinStripes(stripeTop, singleStripe(model) ? "" : stripeBottom) : "";
  const pickStripe = (s) => { setStripe(s); setStripeTouched(true); };
  const archLabel = withStripes && hasArches(model) ? (arch || stripeTop) : "";
  const stripePicker = withStripes ? <StripePicker value={stripeTop} onPick={pickStripe} isDefault={!stripeTouched} bottom={stripeBottom} onPickBottom={setStripeBottom} single={singleStripe(model)} arch={archLabel} onPickArch={setArch} archDefault={!arch} /> : null;
  const colorControls = () => {
    const o = curColorOpt;
    const entry = o && selected[o.id];
    if (!entry) return null;
    const disc = entry.disc || 0;
    const pPub = promoMaps.optPub[o.id] || 0;
    const hasOverride = entry.priceTtc != null && entry.priceTtc !== "";
    const catUnitTtc = priceTtcOf(o, totals.vat);
    const effUnitTtc = hasOverride ? Number(entry.priceTtc) : catUnitTtc * (1 - (disc > 0 ? disc : pPub) / 100);
    return (
      <div className="flex items-center gap-3" data-testid="color-controls">
        <div className="flex items-center gap-0.5" title="Remise sur la couleur (%)">
          <PctInput value={disc > 0 ? disc : (pPub || "")} placeholder="0"
            onChange={(v) => setDisc(o, v)} data-testid="color-disc" className="w-12 h-8 text-xs font-mono px-1.5 text-right" />
          <span className="text-xs text-muted-foreground">%</span>
        </div>
        <div className="flex items-center gap-1" title="Prix TTC (modifiable)">
          <AmountInput value={hasOverride ? entry.priceTtc : Math.round(effUnitTtc)} placeholder={String(Math.round(catUnitTtc * displayCurrency().rate))}
            onChange={(v) => setUnitPrice(o, v)} data-testid="color-unit-price" className="w-20 h-8 text-xs font-mono px-1.5 text-right" />
          <span className="text-xs text-muted-foreground">{sym}</span>
        </div>
        <span className="w-24 text-right whitespace-nowrap">
          {effUnitTtc < catUnitTtc && <span className="block text-[10px] text-muted-foreground line-through leading-none">{euro(catUnitTtc)}</span>}
          <span className="font-mono text-sm" data-testid="color-total">{euro(effUnitTtc)}</span>
        </span>
      </div>
    );
  };
  const palette = (model?.colors || []).filter((c) => c.type !== "special" || colorOptFor(c))
    .map((c, i) => [c, i]).sort(([a, i], [b, j]) => colorRank(a.name, i) - colorRank(b.name, j)).map(([c]) => c);
  const colorPrice = (c) => {
    const o = colorOptFor(c);
    if (!o) return 0;
    const d = selected[o.id]?.disc > 0 ? selected[o.id].disc : (promoMaps.optPub[o.id] || 0);
    return priceTtcOf(o, totals.vat) * (1 - d / 100);
  };
  const curColorOpt = colorOptFor(palette.find((c) => c.name === color));
  const usedNoPrice = rangeKey === "occasion" && !!model && !(Number(model.used?.price_ttc) > 0);
  useEffect(() => {
    if (model?.id && model.image && color && !/ral/i.test(color)) api.post(`/models/${model.id}/color-image`, { color }).catch(() => {});
  }, [model?.id, model?.image, color]);
  const pickColor = (c) => {
    setColor(c.name);
    const opt = colorOptFor(c);
    setSelected((prev) => {
      const next = {};
      for (const [id, e] of Object.entries(prev)) if (!COLOR_OPT_RE.test(normName(e.opt.name))) next[id] = e;
      if (opt) next[opt.id] = { opt, qty: 1, disc: prev[opt.id]?.disc || 0 };
      return next;
    });
  };
  const setDisc = (opt, disc) => {
    const d = Math.max(0, Math.min(100, Number(disc) || 0));
    setSelected((prev) => (prev[opt.id] ? { ...prev, [opt.id]: { ...prev[opt.id], disc: d, priceTtc: undefined } } : prev));
  };
  const setPlace = (opt, side, n) => {
    const max = opt.max_qty ?? 1;
    const cur = placeOf(opt, selected[opt.id]?.qty || 0, selected[opt.id]?.place);
    const other = side === "a" ? "b" : "a";
    const p = { [side]: n, [other]: Math.min(cur[other] || 0, max - n) };
    const qty = p.a + p.b;
    if (qty <= 0) { setQty(opt, 0); return; }
    setSelected((prev) => ({ ...prev, [opt.id]: { ...prev[opt.id], opt, qty, place: p } }));
  };
  const setBroches = (opt, b) => setSelected((prev) => (prev[opt.id] ? { ...prev, [opt.id]: { ...prev[opt.id], broches: b } } : prev));
  const setUnitPrice = (opt, v) => {
    setSelected((prev) => (prev[opt.id] ? { ...prev, [opt.id]: { ...prev[opt.id], priceTtc: v === "" ? undefined : Math.max(0, Number(v) || 0) } } : prev));
  };
  const typeLabelMap = { van: "Van chevaux", fourgon: "Remorque fourgon", autre: "Autre" };
  const makeReprise = () => ({ type: rangeKey === "van" ? "van" : "fourgon", montant: 0, tvaRecup: false, ...Object.fromEntries(REPRISE_KEYS.map((k) => [k, ""])) });
  const addReprise = () => setTradeIns((xs) => [...xs, makeReprise()]);
  const removeReprise = (i) => setTradeIns((xs) => xs.filter((_, idx) => idx !== i));
  // Fautras horse vans from 2002: polyethylene floor in good condition by default (only if floor fields are empty)
  const floorDefault = (x0) => {
    const x = x0.plancher === "Refait à neuf" && x0.plancher_type !== "Bois" ? { ...x0, plancher: "" } : x0;
    const year = parseInt(String(x.annee || "").match(/\d{4}/)?.[0] || "0", 10);
    return (x.type === "van" && x.marque === "Fautras" && year >= 2002 && !x.plancher_type && !x.plancher)
      ? { ...x, plancher_type: "Polyéthylène", plancher: "Bon état" } : x;
  };
  const setReprise = (i, k, v) => setTradeIns((xs) => xs.map((x, idx) => (idx === i ? floorDefault({ ...x, [k]: v }) : x)));
  const patchReprise = (i, patch) => setTradeIns((xs) => xs.map((x, idx) => (idx === i ? floorDefault({ ...x, ...patch }) : x)));

  const vatPct = Math.round((totals.vat || 0) * 1000) / 10;

  const buildExpo = (salon, resellerIds, phone = "") => {
    const vat = totals.vat;
    const options = Object.values(selected).filter((e) => e.qty > 0).map((e) => {
      const cat = priceTtcOf(e.opt, vat);
      const effDisc = (e.disc || 0) > 0 ? e.disc : (promoMaps.optPub[e.opt.id] || 0);
      const unit = (e.priceTtc != null && e.priceTtc !== "") ? Math.max(0, Number(e.priceTtc)) : cat * (1 - effDisc / 100);
      const base = rangeKey === "van" ? stripParen(e.opt.name) : e.opt.name;
      const pl = isPlaceOpt(e.opt) ? ` — ${placeLabel(e.opt, placeOf(e.opt, e.qty, e.place))}` : "";
      return { name: base + pl, qty: e.qty, cat_ttc: cat * e.qty, salon_ttc: unit * e.qty };
    });
    for (const s of specials) if (s.name && Number(s.priceTtc) > 0) options.push({ name: s.name, qty: 1, cat_ttc: Number(s.priceTtc), salon_ttc: spNet(s) });
    const activePromos = modelPromos.filter((p) => activePromoIds.has(p.id));
    const ctry = countries.find((c) => c.code === countryCode);
    const rangeName = ranges.find((r) => r.key === rangeKey)?.name || "";
    const poster = {
      salon, range_name: rangeName, model_id: model.id, model_name: model.name,
      model_name_short: rangeKey === "van" ? stripParen(model.name) : model.name, model_image: model.image || "", model_details: model.details || "", available_colors: (model.colors || []).filter((c) => c.type !== "special" && !/autre|ral/i.test(c.name || "")).map((c) => c.name).sort((a, b) => colorRank(a, 0) - colorRank(b, 0)), range_key: rangeKey,
      country_code: countryCode, country_name: ctry?.name || "", vat_pct: vatPct,
      promo_names: activePromos.map((p) => p.name), color: colorLabel, stripe_color: stripeLabel, arch_color: archLabel,
      model_cat_ttc: totals.catModelTtc, model_salon_ttc: totals.modelAfterTtc, options,
      total_cat_ttc: totals.subBeforeTtc, total_salon_ttc: totals.totalAfterTtc, phone, ptac: ptacEff, ptac_range: f2Range,
      standard_equipment: model.standard_equipment || [], empty_weight: model.empty_weight || 0, show_empty_weight: localStorage.getItem("fautras_poster_empty_weight") === "true",
    };
    const numOrNull = (v) => (v === "" || v == null ? null : Number(v));
    const payload = {
      name: salon, range_key: rangeKey, range_name: rangeName, model_id: model.id, model_name: model.name,
      options: Object.values(selected).filter((e) => e.qty > 0).map((e) => ({ id: e.opt.id, qty: e.qty, disc: Number(e.disc) || 0, price_ttc: numOrNull(e.priceTtc), broches: e.broches || "", place: e.place || {} })),
      promo_id: activePromos[0]?.id || "", promo_name: activePromos.map((p) => p.name).join(", "),
      promo_ids: activePromos.map((p) => p.id), country_code: countryCode, poster,
      special_options: specials.filter((s) => s.name || (Number(s.priceTtc) || 0) > 0).map((s) => ({
        name: s.name || "Option spéciale", price_ttc: Number(s.priceTtc) || 0, discount_pct: spDisc(s),
        description: s.description || "", attachment_url: s.attachmentUrl || "", attachment_name: s.attachmentName || "" })),
      model_after: numOrNull(modelAfter), options_after: numOrNull(optionsAfter), total_after: numOrNull(totalAfter), ptac: ptacEff, color: colorLabel, stripe_color: stripeLabel, arch_color: archLabel,
      reseller_ids: user?.role === "admin" ? resellerIds : [],
    };
    return { payload, poster };
  };
  const createPoster = async (salon, resellerIds, phone) => {
    const { payload, poster } = buildExpo(salon, resellerIds, phone);
    try {
      await api.post("/expos", payload);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Échec de l'enregistrement");
      return;
    }
    setPosterOpen(false);
    toast.success(tr("Affiche créée et enregistrée dans Véhicules à la vente"));
    setPosterPdf(await generatePosterPdf(poster, { preview: true }));
  };
  const canUpdateExpo = !!(expoId && expoConfig && !expoConfig._quote && (user?.role === "admin" || expoConfig.created_by === user?.id));
  const [updatingExpo, setUpdatingExpo] = useState(false);
  const updatePosterRef = useRef(null);
  useEffect(() => {
    if (searchParams.get("reprice") !== "1" || !cfgReady || !canUpdateExpo || repricedRef.current || promosFor !== countryCode) return;
    if (expoConfig.range_key === "van" && pricing?.country !== expoConfig.country_code) {
      if (pricing && !(pricing.allowed || []).includes(expoConfig.country_code)) {
        repricedRef.current = true;
        toast.error(tr("Pas de tarif pour ce pays : prix de l'affiche inchangé"));
        navigate("/expos");
      }
      return;
    }
    repricedRef.current = true;
    const tm = setTimeout(async () => {
      const ok = await updatePosterRef.current?.();
      if (ok) navigate("/expos");
    }, 1500);
    return () => { clearTimeout(tm); repricedRef.current = false; };
  }, [cfgReady, pricing, canUpdateExpo, promosFor]); // eslint-disable-line react-hooks/exhaustive-deps
  const updatePoster = async () => {
    const { payload, poster } = buildExpo(expoConfig.name, expoConfig.reseller_ids || [], (expoConfig.poster || {}).phone || "");
    const old = expoConfig.poster || {};
    if (old.original_image != null) { poster.original_image = old.original_image; poster.model_image = old.model_image; }
    poster.salon = old.salon || expoConfig.name;
    setUpdatingExpo(true);
    try {
      const keepPromos = searchParams.get("reprice") === "1" ? { promo_ids: [...new Set([...(payload.promo_ids || []), ...(expoConfig.promo_ids || [])])] } : {};
      const { data } = await api.put(`/expos/${expoId}`, { ...payload, ...keepPromos, reseller_ids: expoConfig.reseller_ids || [] });
      Object.assign(expoConfig, data);
      toast.success(tr("Affiche mise à jour"));
      if (searchParams.get("reprice") === "1") return true;
      setPosterPdf(await generatePosterPdf(poster, { preview: true }));
      return true;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Échec de la mise à jour");
      return false;
    } finally { setUpdatingExpo(false); }
  };
  updatePosterRef.current = updatePoster;
  const totalTradeIn = tradeIns.reduce((s, t) => s + (Number(t.montant) || 0), 0);
  const tradeInEnabled = totalTradeIn > 0;
  const filledTradeIns = tradeIns.filter((t) => (Number(t.montant) || 0) > 0 || (t.photos || []).length > 0 || REPRISE_KEYS.some((k) => (t[k] || "").toString().trim()));
  useEffect(() => { if (tradeIns.length === 0) setTradeIns([makeReprise()]); }, [tradeIns.length]); // eslint-disable-line react-hooks/exhaustive-deps
  const repriseHt = tradeIns.reduce((s, t) => s + ((Number(t.montant) || 0) / (t.tvaRecup ? (1 + totals.vat) : 1)), 0);
  // Horse Trailer Price: estimate in a popup (SSO) and receive the result back by postMessage
  const htpCrit = useHtpCriteria();
  const estimRef = useRef(0);
  const popupRef = useRef(null);
  const applyHtp = (msg, idx) => {
    const { patch, photos } = htpToReprise(msg, htpCrit, totals.vat);
    setTradeIns((xs) => {
      const arr = xs.length ? [...xs] : [makeReprise()];
      const j = Math.min(Math.max(0, idx), arr.length - 1);
      const cur = arr[j];
      const known = new Set((cur.photos || []).map((p) => p.url));
      arr[j] = floorDefault({ ...cur, ...patch, photos: [...(cur.photos || []), ...photos.filter((u) => !known.has(u)).map((url, k) => ({ url, name: `Photo HTP ${k + 1}` }))] });
      return arr;
    });
    const c = htpToClient(msg.client || {});
    if (Object.keys(c).length) setClient((p) => ({ ...p, ...c }));
    setOpenCats((o) => Array.from(new Set([...(o ?? []), "__tradein__"])));
    toast.success(`${tr("Reprise remplie depuis Horse Trailer Price")}${patch.montant ? ` : ${euroInt(patch.montant)} ${tr("TTC")}` : ""}`, { duration: 8000 });
  };
  const applyHtpRef = useRef(applyHtp);
  applyHtpRef.current = applyHtp;
  useEffect(() => {
    const onMsg = (e) => {
      if (!HTP_ORIGINS.includes(e.origin) || !e.data || e.data.type !== "fautras-reprise") return;
      if (!popupRef.current || e.source !== popupRef.current) return;
      applyHtpRef.current(e.data, estimRef.current);
    };
    window.addEventListener("message", onMsg);
    return () => { window.removeEventListener("message", onMsg); };
  }, []);
  const htpImportDone = useRef(false);
  useEffect(() => {
    if (htpImportDone.current || !htpCrit.ready || !modelId || searchParams.get("htp") !== "1") return;
    const raw = sessionStorage.getItem("fautras_htp_import");
    htpImportDone.current = true;
    if (!raw) return;
    sessionStorage.removeItem("fautras_htp_import");
    try { applyHtpRef.current(JSON.parse(raw), 0); } catch { /* ignore malformed payload */ }
  }, [htpCrit.ready, modelId, searchParams]);
  const estimateReprise = async (i) => {
    const ti = tradeIns[i];
    const w = window.open("about:blank", "htp_estimation");
    if (!w) { toast.error(tr("Fenêtre bloquée : autorisez les fenêtres pop-up pour ce site.")); return; }
    estimRef.current = i;
    popupRef.current = w;
    try {
      const { data } = await api.post("/htp/sso-token");
      w.location.href = buildEstimateUrl(ti, client, data.token, htpCrit);
    } catch (err) {
      w.close();
      toast.error(tr(formatApiErrorDetail(err.response?.data?.detail) || "Impossible d'ouvrir Horse Trailer Price."));
    }
  };
  const sellerInFrance = user?.role === "admin" || !(user?.sectors || []).length || (user?.sectors || []).some((s) => s.country === "FR");
  const regOn = countryCode === "FR" && sellerInFrance;
  const regLocked = rangeKey === "van";
  const regKeys = regLocked ? REG_FEES.map((r) => r.key) : regSel ?? (rangeKey === "occasion" ? ["cg", "admin"] : ["cg", "plate", "admin"]);
  const regFees = REG_FEES.map((r) => {
    const pubKey = r.key === "admin" ? "reg_admin_public" : `reg_${r.key}`;
    const ttc = regAmounts[pubKey] != null ? Number(regAmounts[pubKey]) : r.ttc;
    const resTtc = regAmounts[`reg_${r.key}`] != null ? Number(regAmounts[`reg_${r.key}`]) : (r.resTtc ?? r.ttc);
    return { ...r, ttc, resTtc };
  });
  const feeOf = (k) => regFees.find((r) => r.key === k);
  const regGroups = [
    { id: "cg", keys: ["cg"], label: feeOf("cg").label, ttc: feeOf("cg").ttc, noVat: true },
    { id: "admin", keys: ["plate", "admin"], label: "Frais administratifs et de plaque TTC", ttc: feeOf("plate").ttc + feeOf("admin").ttc, noVat: false },
  ];
  const regLines = regOn ? regFees.filter((r) => regKeys.includes(r.key)) : [];
  const regFullTtc = regLines.reduce((s, r) => s + r.ttc, 0);
  const regFullHt = regLines.reduce((s, r) => s + r.ttc / (1 + r.vat), 0);
  const regResHt = regLines.reduce((s, r) => s + r.resTtc / (1 + r.vat), 0);
  const regDisc = Math.min(Math.max(0, Number(regDiscount) || 0), regFullTtc);
  const regDiscHt = regFullTtc ? regDisc * regFullHt / regFullTtc : 0;
  const regTtc = regFullTtc - regDisc;
  const regHt = regFullHt - regDiscHt;
  const netTtc = Math.max(0, totals.totTtc + regTtc - totalTradeIn);
  const acompteAuto = Math.min(Math.round(defaultDepositPct / 100 * totals.totTtc), netTtc);
  const acompteValue = (acompte !== "" && acompte != null) ? Math.min(Math.max(0, Number(acompte)), netTtc) : acompteAuto;
  const acomptePct = depositPct(acompteValue, totals.totTtc);
  // Affichage « HT uniquement » (par devis)
  const vatK = 1 + totals.vat;
  const vh = (v) => (htOnly ? v / vatK : v);
  const netHt = Math.max(0, totals.totPubHt - repriseHt) + regHt;
  const payK = htOnly ? (netTtc > 0 ? netHt / netTtc : 1 / vatK) : 1;
  const feeAmt = (k) => (htOnly ? feeOf(k).ttc / (1 + feeOf(k).vat) : feeOf(k).ttc);
  const [depPctDraft, setDepPctDraft] = useState(null);

  useEffect(() => {
    const names = expoConfig?.promo_names || [];
    if (!names.length || !promos.length) return;
    setActivePromoIds(new Set(promos.filter((p) => names.includes(p.name)).map((p) => p.id)));
  }, [promos, expoConfig]);
  const offerValidUntil = () => {
    const d = new Date(); d.setDate(d.getDate() + 7);
    const week = d.toISOString().slice(0, 10);
    const ends = modelPromos.filter((p) => activePromoIds.has(p.id) && p.end_date).map((p) => p.end_date.slice(0, 10)).sort();
    return ends.length && ends[0] < week ? ends[0] : week;
  };
  const fmtFR = (iso) => { if (!iso || iso.length < 10) return iso || ""; const [y, m, d] = iso.slice(0, 10).split("-"); return `${d}/${m}/${y}`; };

  const buildPayload = () => ({
    draft_id: localStorage.getItem("fautras_draft_id") || "",
    replaces_id: replacesId,
    range_key: rangeKey,
    range_name: currentRange?.name || "",
    model_id: modelId === USED_DRAFT_ID ? "" : model.id,
    model_name: t(stripParen(model.name)),
    lang,
    model_reseller_ht: model.reseller_ht * (1 - (promoMaps.modelRes || 0) / 100),
    model_public_ht: totals.modelAfterTtc / (1 + totals.vat),
    model_public_ttc: totals.modelAfterTtc,
    model_catalog_ttc: totals.catModelTtc,
    standard_equipment: rangeKey === "occasion" || modelId === USED_DRAFT_ID ? [] : equipmentOf({ model_details: model.details || "", model_name: model.name, standard_equipment: model.standard_equipment || [], range_key: rangeKey })
      .filter((s) => !(model.colors || []).some((c) => (c.name || "").trim().toLowerCase() === s.trim().toLowerCase()) && !/^(blanc|gris|noir|bleu|rouge|vert|beige|ivoire|taupe)\b/i.test(s.trim())),
    options: [...Object.values(selected).map(({ opt, qty, disc, priceTtc, broches, place }) => {
      const cam = /trailer cam/i.test(opt.name);
      const pl = isPlaceOpt(opt) ? placeOf(opt, qty, place) : null;
      const vat = totals.vat;
      const catUnitTtc = priceTtcOf(opt, vat);
      const pPub = promoMaps.optPub[opt.id] || 0;
      const pRes = promoMaps.optRes[opt.id] || 0;
      const effDisc = (disc || 0) > 0 ? disc : pPub;
      const hasOverride = priceTtc != null && priceTtc !== "";
      const effUnitTtc = hasOverride ? Number(priceTtc) : catUnitTtc * (1 - effDisc / 100);
      return {
        id: opt.id,
        name: (qty > 1 ? `${t(opt.name)} (x${qty})` : t(opt.name)) + (cam ? ` — ${t("Véhicule tracteur en")} ${broches || "13"} ${t("broches")}` : "") + (pl ? ` — ${placeLabel(opt, pl)}` : ""),
        qty,
        ...(cam ? { broches: broches || "13" } : {}),
        ...(pl ? { place: pl } : {}),
        reseller_ht: opt.reseller_ht * qty * (1 - pRes / 100),
        public_ht: (effUnitTtc / (1 + vat)) * qty,
        public_ttc: effUnitTtc * qty,
        catalog_ttc: catUnitTtc * qty,
        discount_pct: effDisc || 0,
      };
    }), ...specials.filter((s) => s.name || (Number(s.priceTtc) || 0) > 0).map((s) => {
      const ttc = spNet(s);
      const ht = totals.vat != null ? ttc / (1 + totals.vat) : ttc;
      return { id: s.id, name: s.name || "Option spéciale", reseller_ht: ht, public_ht: ht, public_ttc: ttc, catalog_ttc: Number(s.priceTtc) || 0, discount_pct: spDisc(s) };
    })],
    special_options: specials.filter((s) => s.name || (Number(s.priceTtc) || 0) > 0).map((s) => ({
      name: s.name || "Option spéciale", price_ttc: Number(s.priceTtc) || 0, discount_pct: spDisc(s),
      description: s.description || "", attachment_url: s.attachmentUrl || "", attachment_name: s.attachmentName || "",
    })),
    discount_pct: 0,
    discount_amount: Math.max(0, (totals.modelAfterTtc + totals.optEffTtc) - totals.totalAfterTtc),
    deposit: acompteValue,
    deposit_methods: depositPay,
    balance_methods: balancePay,
    financing: countryCode === "FR" && financing ? financing : {},
    used: rangeKey === "occasion" ? { ...(model?.used || {}), warranty_line: warrantyLine(model?.used || {}) } : {},
    trade_in: totalTradeIn,
    reg_fees: regOn ? regKeys : [],
    reg_discount: regOn ? Math.round(regDisc * 100) / 100 : 0,
    trade_in_label: filledTradeIns.length ? [filledTradeIns[0].marque, filledTradeIns[0].modele, filledTradeIns[0].annee].filter(Boolean).join(" ") : "",
    trade_in_details: filledTradeIns.length ? { ...filledTradeIns[0], type_label: typeLabelMap[filledTradeIns[0].type], tva_recuperable: filledTradeIns[0].tvaRecup, prix_reprise_ht: (Number(filledTradeIns[0].montant) || 0) / (filledTradeIns[0].tvaRecup ? (1 + totals.vat) : 1) } : {},
    trade_ins: filledTradeIns.map((t) => ({ type: t.type, type_label: typeLabelMap[t.type], montant_ttc: Number(t.montant) || 0, tva_recuperable: !!t.tvaRecup, prix_reprise_ht: (Number(t.montant) || 0) / (t.tvaRecup ? (1 + totals.vat) : 1), photos: t.photos || [], ...(t.htp ? { htp: t.htp, estimation_id: t.htp.estimation_id || "" } : {}), ...Object.fromEntries(REPRISE_KEYS.map((k) => [k, (k === "places" && t.type !== "van") || (k === "essieux" && t.type === "van") ? "" : (t[k] || REPRISE_BEST[k] || "")])) })),
    country_code: countryCode,
    price_country: pricing?.country || countryCode,
    currency: pricing?.currency || "EUR",
    fx_rate: pricing?.rate || 1,
    fx_date: pricing?.rate_date || "",
    country_name: (countries.find((c) => c.code === countryCode) || {}).name || "France",
    vat_rate: totals.vat ?? 0.2,
    client: { name: client.name, firstname: client.firstname, company: client.company, email: client.email, phone: [client.phone_code, client.phone].filter(Boolean).join(" "),
      address: [client.address, client.postal, client.city, client.country].filter(Boolean).join(", "),
      street: client.address || "", postal: client.postal || "", city: client.city || "", country: client.country || "",
      client_type: client.company?.trim() ? "societe" : (client.client_type || ""), siret: client.siret || "" },
    client_id: localStorage.getItem("fautras_client_id") || "",
    client_meta: { address: client.address, postal: client.postal, city: client.city },
    notes: notes,
    valid_until: offerValidUntil(),
    delivery_place: deliveryPlace.trim(),
    delivery_delay: deliveryDelay.trim(),
    ptac: ptacEff,
    delay_imperative: !!deliveryDelay.trim() && delayImperative,
    vehicle_source: vehicleSource || ((expoConfig && !expoConfig._quote) || rangeKey === "occasion" ? "stock" : ""),
    stock_location: stockLocation.trim(),
    serial_number: serialNumber ?? expoSerial,
    stock_expo_id: expoConfig && !expoConfig._quote ? expoConfig.id || "" : (expoConfig?._quote?.stock_expo_id || ""),
    color: colorLabel,
    stripe_color: stripeLabel,
    arch_color: archLabel,
    aid_mode: aidMode,
    ht_only: htOnly,
    promo_name: modelPromos.filter((p) => activePromoIds.has(p.id)).map((p) => p.name).join(", "),
    signature: "",
    signed_at: "",
    cgv_accepted_at: "",
  });

  const lastSavedRef = useRef(null);
  const baselineRef = useRef(null);
  const snapshot = (p) => { const { draft_id: _d, ...rest } = p; return JSON.stringify(rest); };
  const saveQuote = async (silent = false) => {
    if (!model) { if (!silent) toast.error(tr("Sélectionnez un modèle")); return null; }
    if (usedNoPrice) { if (!silent) toast.error(tr("Prix à définir : renseignez le prix de vente de l'occasion avant de faire le devis.")); return null; }
    const badSpecial = specials.find((s) => ((s.name || "").trim() || Number(s.priceTtc) > 0) && !((s.description || "").trim()));
    if (badSpecial) { if (!silent) toast.error(tr("Veuillez renseigner une description pour chaque option spéciale.")); return null; }
    if (lockedRef.current) { if (!silent) toast.error(tr("Commande signée : les modifications ne sont plus prises en compte.")); return null; }
    setSaving(true);
    try {
      ensureDraftId();
      const payload = buildPayload();
      const { data } = await api.post("/quotes", payload);
      setSavedQuote(data);
      lastSavedRef.current = snapshot(payload);
      localStorage.setItem("fautras_draft_id", data.id);
      clearPending();
      toast.dismiss("autosave-failed");
      if (!silent) toast.success(`Devis ${data.quote_number} enregistré`);
      return data;
    } catch (err) {
      if (!err.response) {
        // Hors ligne : devis enregistré sur l'appareil, envoyé et numéroté au retour du réseau
        ensureDraftId();
        const payload = buildPayload();
        const prov = provisionalQuote(payload);
        markPending(payload);
        lastSavedRef.current = snapshot(payload);
        setOfflineSaved(prov);
        toast.success("Devis enregistré sur l'appareil (hors ligne)", {
          id: "offline-saved", duration: 12000,
          description: "Il sera envoyé au serveur et numéroté dès le retour de la connexion.",
          action: { label: "PDF provisoire", onClick: () => openProvisionalPdf(prov) },
        });
        return null;
      }
      if (!silent) toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      else notifySaveFailed(buildPayload(), retryPending);
      return null;
    } finally {
      setSaving(false);
    }
  };
  // Manager / admin : changement de revendeur sans perdre la configuration ni le client
  const switchRef = useRef(null);
  switchRef.current = async (next) => {
    if (!model || usedNoPrice || !(client.name || "").trim()) return null;
    ensureDraftId();
    const payload = buildPayload();
    const { data } = await api.post("/quotes", { ...payload, change_reseller: true }, next ? { headers: { "X-Act-As": next.id }, noActAs: true } : { noActAs: true });
    lastSavedRef.current = snapshot(payload);
    localStorage.setItem("fautras_draft_id", data.id);
    clearPending();
    return `/configurer?quote=${data.id}&rs=1`;
  };
  useEffect(() => {
    setSwitchHook((next) => switchRef.current(next));
    return () => { setSwitchHook(null); };
  }, []);
  // Enregistrement automatique en quittant (si coordonnées client remplies et configuration modifiée)
  const autoSaveRef = useRef(null);
  autoSaveRef.current = (unloading) => {
    if (!model || usedNoPrice || !(client.name || "").trim() || saving || lockedRef.current) return;
    if ((specials || []).some((s) => ((s.name || "").trim() || Number(s.priceTtc) > 0) && !((s.description || "").trim()))) return;
    const snap = snapshot(buildPayload());
    if (snap === lastSavedRef.current || snap === baselineRef.current) return;
    ensureDraftId();
    const payload = buildPayload();
    if (!unloading) { saveQuote(true); return; }
    lastSavedRef.current = snap;
    markPending(payload);
    const act = getActAs()?.id;
    const actHdr = act ? { "X-Act-As": act } : {};
    if (unloading === "unmount") { api.post("/quotes", payload, { headers: actHdr }).then(() => clearPending(payload.draft_id)).catch(() => notifySaveFailed(payload, retryPending)); return; }
    fetch(`${api.defaults.baseURL}/quotes`, { method: "POST", keepalive: true, credentials: "include", headers: { "Content-Type": "application/json", ...actHdr }, body: JSON.stringify(payload) }).then((r) => { if (r.ok) clearPending(payload.draft_id); }).catch(() => {});
  };
  useEffect(() => {
    const onHide = () => { if (document.visibilityState === "hidden") autoSaveRef.current?.(false); };
    const onUnload = () => autoSaveRef.current?.(true);
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", onUnload);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", onUnload);
      autoSaveRef.current?.("unmount");
    };
  }, []);
  useEffect(() => {
    if (!quoteParam || !modelId || baselineRef.current || searchParams.get("rs")) return undefined;
    const t = setTimeout(() => { if (!baselineRef.current) baselineRef.current = snapshot(buildPayload()); }, 2500);
    return () => clearTimeout(t);
  }, [quoteParam, modelId, availOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitQuoteForm = async () => {
    const q = await saveQuote();
    if (q) setFormOpen(false);
  };
  // PDF « provisoire » calculé sur l'appareil (sans réseau)
  const provisionalQuote = (payload) => {
    const act = getActAs();
    return {
      ...payload, id: payload.draft_id, quote_number: "PROVISOIRE", provisional: true, created_at: new Date().toISOString(),
      total_public_ttc: totals.totTtc, total_public_ht: totals.totPubHt, total_tva: totals.totTtc - totals.totPubHt,
      reg_fee_lines: regLines.map((r) => ({ key: r.key, label: r.label, vat_rate: r.vat, ttc: r.ttc, ht: r.ttc / (1 + r.vat) })),
      reg_fees_ttc: regTtc, net_to_pay_ttc: netTtc, net_to_pay_ht: netHt,
      company: regAmounts || {},
      reseller_name: act?.name || user?.name || "",
      ...(act ? {} : { reseller_email: user?.email || "", reseller_company: user?.company || "", reseller_phone: user?.phone || "", reseller_address: user?.address || "" }),
    };
  };
  const openProvisionalPdf = async (prov) => {
    const company = prov.company?.company ? prov.company : await api.get("/company").then((r) => r.data).catch(() => ({}));
    try { await generateQuotePdf({ ...prov, company }, { preview: true }); } catch (e) { toast.error("PDF provisoire impossible : " + (e.message || "")); }
  };

  const [ribAsk, setRibAsk] = useState(null);
  const contactMissing = (email = client.email) => [!(client.name || "").trim() && "nom", !(client.firstname || "").trim() && "prénom", !(client.address || "").trim() && "adresse", !(client.city || "").trim() && "ville", !((client.postal || "").trim() || /\b\d{4,5}\b|\b[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}\b/.test((client.address || "").toUpperCase())) && "code postal", (client.phone || "").replace(/\D/g, "").length < 6 && "téléphone",
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || "").trim()) && "email"].filter(Boolean);
  const contactOk = (email) => {
    const m = contactMissing(email);
    if (m.length) {
      toast.error(`${tr("Coordonnées du client incomplètes")} (${m.map((x) => tr(x)).join(", ")}) : ${tr("complétez-les avant d'envoyer le devis.")}`);
      setMissing(m); setSignOpen(false); setFormOpen(true);
    }
    return !m.length;
  };
  const red = (k) => (missing.includes(k) && contactMissing().includes(k) ? "border-destructive ring-1 ring-destructive focus-visible:ring-destructive" : "");
  const contactCheckRef = useRef(false);
  useEffect(() => {
    if (contactCheckRef.current || searchParams.get("contact") !== "1" || !client.name) return;
    contactCheckRef.current = true;
    contactOk();
  }, [client.name]); // eslint-disable-line react-hooks/exhaustive-deps
  const sendEmail = () => contactOk() && setRibAsk(() => doSendEmail);
  const doSendEmail = async (ribPath, bank) => {
    const q = savedQuote || (await saveQuote());
    if (!q) return;
    const t = toast.loading("Envoi du devis par email…");
    try {
      const data = await sendQuotePdfEmail(q.id, ribPath, bank);
      if (data.simulated && !data.count) toast.dismiss(t);
      else toast.success(`Devis envoyé à ${data.count} destinataire(s)`, { id: t });
      setSavedQuote({ ...q, status: data.status || q.status });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message, { id: t });
    }
  };

  const colorMissing = palette.length > 0 && (!color || (/ral/i.test(color) && !ralText.trim()));
  const requireColor = () => {
    if (!colorMissing) return true;
    toast.error(color ? "Veuillez préciser la couleur RAL avant de passer commande." : "Veuillez choisir une couleur avant de passer commande.");
    return false;
  };
  const [openCats, setOpenCats] = useState(null);
  const [centerMsg, setCenterMsg] = useState("");
  useEffect(() => {
    if (!centerMsg) return;
    const t = setTimeout(() => setCenterMsg(""), 3500);
    return () => clearTimeout(t);
  }, [centerMsg]);
  const goTo = (cat, testid, msg, big = false) => {
    setFormOpen(false); setSignOpen(false);
    if (cat) setOpenCats((c) => Array.from(new Set([...(c ?? Object.keys(optionsByCat).slice(0, 1)), cat])));
    if (big) setCenterMsg(tr(msg)); else toast.error(tr(msg), { duration: 6000 });
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="${testid}"]`);
      if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.focus?.(); }
    }, 350);
    return false;
  };
  const openFinancing = () => {
    const w = Math.min(1100, window.screen.availWidth - 40), h = Math.min(900, window.screen.availHeight - 60);
    const left = Math.max(0, (window.screen.availWidth - w) / 2), top = Math.max(0, (window.screen.availHeight - h) / 2);
    const cents = (v) => String(Math.round(v * 100) / 100);
    const solde = netTtc - acompteValue, prix = totals.totalAfterTtc;
    const apport = Math.round(Math.max(0, prix - solde) * 100) / 100;
    finOffsetRef.current = { acompte: acompteValue, apport, net: netTtc };
    const p = new URLSearchParams({ prix: cents(prix), apport: cents(apport), montant: cents(solde), retour: "devis" });
    const tel = (client.phone || "").trim() ? (/^\s*(\+|00)/.test(client.phone) ? client.phone.trim() : `${client.phone_code || "+33"} ${client.phone.trim()}`) : "";
    const isUsed = rangeKey === "occasion";
    const usedType = model?.used?.type;
    const gamme = isUsed ? (usedType === "van" ? "van" : usedType === "fourgon" ? "remorque" : "autre") : (rangeKey === "van" ? "van" : "remorque");
    const HORSES = { 15: "1,5", p15: "1,5", droit: "2", diag2: "2", diag3: "3", diag4: "4", diag5: "5" };
    const chevaux = gamme !== "van" ? "" : isUsed ? ((model?.used?.places || "").match(/\d+(,\d+)?/)?.[0] || "") : (HORSES[modelGroup] || HORSES[group] || "");
    p.set("etat", isUsed ? "occasion" : "neuf");
    p.set("gamme", gamme);
    p.set("reprise", filledTradeIns.length ? "oui" : "non");
    if (totalTradeIn > 0) p.set("montant_reprise", cents(totalTradeIn));
    if (chevaux) p.set("chevaux", chevaux);
    const annee = isUsed ? (String(model?.used?.year || "").match(/\d{4}/)?.[0] || "") : "";
    if (annee) p.set("annee", annee);
    p.set("achat", "immediat");
    [["nom", client.name], ["prenom", client.firstname], ["societe", client.company], ["email", client.email], ["tel", tel],
      ["adresse", client.address], ["cp", client.postal], ["ville", client.city], ["pays", client.country]].forEach(([k, v]) => { if ((v || "").trim()) p.set(k, v.trim()); });
    const url = `${process.env.REACT_APP_FINANCEMENT_URL}/?${p.toString()}`;
    window.open(url, "fautras-financement", `popup=yes,width=${w},height=${h},left=${left},top=${top},scrollbars=yes,resizable=yes`);
  };
  const checkRequired = () => {
    if (!deliveryPlace.trim()) return goTo(null, "delivery-block", "Veuillez choisir le lieu de livraison.", true);
    if (colorMissing) return goTo(optionsByCat.STYLE ? "STYLE" : null, "color-picker", color ? "Veuillez préciser la couleur RAL." : "Veuillez choisir une couleur.", true);
    for (let i = 0; i < tradeIns.length; i++) {
      const t = tradeIns[i];
      if (!filledTradeIns.includes(t)) continue;
      const miss = [
        [!t.marque, "marque", "la marque"],
        [t.type === "van" && !t.places, "places", "le nombre de places"],
        [!String(t.annee || "").trim(), "annee", "l'année"],
        [!(Number(t.montant) > 0), "montant", "le prix de reprise TTC"],
      ].find(([m]) => m);
      if (miss) return goTo("__tradein__", `reprise-${i}-${miss[1]}`, `${tr("Reprise")} ${i + 1} : ${tr("veuillez renseigner")} ${tr(miss[2])}.`);
    }
    return true;
  };
  const photoAck = useRef(false);
  const [photoPrompt, setPhotoPrompt] = useState(null);
  const needPhoto = (cont) => {
    if (photoAck.current) return false;
    const i = tradeIns.findIndex((t) => filledTradeIns.includes(t) && !(t.photos || []).length);
    if (i < 0) return false;
    setFormOpen(false); setSignOpen(false);
    setPhotoPrompt({ i, cont });
    return true;
  };
  const openSignView = async () => {
    if (!checkRequired() || needPhoto(openSignView)) return;
    if (!vehicleSource && !((expoConfig && !expoConfig._quote) || rangeKey === "occasion")) {
      goTo(null, "vehicle-source-block", "Indiquez « Véhicule en stock » ou « Véhicule à fabriquer » avant la signature.", true);
      return;
    }
    const q = navigator.onLine === false ? null : await saveQuote();
    if (q) { setSignQuote(q); setFormOpen(false); setSignOpen(true); }
    else if (navigator.onLine === false) { setSignQuote(provisionalQuote(buildPayload())); setFormOpen(false); setSignOpen(true); }
  };

  const ensureSource = async (q) => {
    if (q.stock_order) return q;
    const v = vehicleSource || ((expoConfig && !expoConfig._quote) || rangeKey === "occasion" ? "stock" : "");
    if (q.vehicle_source && (!v || v === q.vehicle_source)) return q;
    if (!v) {
      goTo(null, "vehicle-source-block", "Indiquez « Véhicule en stock » ou « Véhicule à fabriquer » avant la signature.", true);
      return null;
    }
    await api.put(`/quotes/${q.id}/vehicle-source`, { vehicle_source: v, stock_location: stockLocation.trim() });
    return { ...q, vehicle_source: v };
  };
  const copyLink = async () => {
    const q = signQuote || savedQuote;
    if (!q) return;
    if (!contactOk()) return;
    try {
      if (await copySignLink(q, ensureSource)) toast.success(tr("Lien du devis copié : collez-le dans votre message au client"));
    } catch (err) { if (err.message !== "cancelled") toast.error(formatApiErrorDetail(err.response?.data?.detail) || tr("Impossible de copier le lien")); }
  };
  const sendSignLink = (clientEmail) => contactOk(clientEmail) && setRibAsk(() => (ribPath, bank) => doSendSignLink(clientEmail, ribPath, bank));
  const doSendSignLink = async (clientEmail, ribPath, bank) => {
    const q0 = signQuote || savedQuote;
    if (!q0) return;
    setSaving(true);
    const t = toast.loading("Envoi du lien de signature au client…");
    try {
      const q = await ensureSource(q0);
      if (!q) { toast.dismiss(t); return; }
      const { data } = await api.post(`/quotes/${q.id}/sign-link`, { client_email: clientEmail, rib_path: ribPath, bank: !!bank });
      setClient((c) => ({ ...c, email: clientEmail }));
      toast.success(`Lien de signature envoyé à ${data.sent_to}`, { id: t });
      setSignOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message, { id: t });
    } finally { setSaving(false); }
  };

  const stockDraftRef = useRef("");
  const signedRef = useRef(false);
  const lockedRef = useRef(false);
  const isAgent = user?.dealer_type === "agent";
  const openStockOrder = async () => {
    if (!checkRequired()) return;
    setSaving(true);
    try {
      const { data } = await api.post("/quotes", { ...buildPayload(), draft_id: stockDraftRef.current, stock_order: true });
      stockDraftRef.current = data.id;
      setSignQuote(data); setSignOpen(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };
  const addStockOrderExpo = async (q) => {
    const { payload } = buildExpo(q.delivery_place || deliveryPlace.trim() || tr("Stock"), []);
    try { await api.post("/expos", { ...payload, source_quote_id: q.id }); toast.success(tr("Véhicule ajouté à vos véhicules à la vente")); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || tr("Véhicule non ajouté au stock")); }
  };

  // Signature sans réseau : commande gardée sur l'appareil, enregistrée + numérotée + envoyée au retour de la connexion
  const signOffline = (signature, cgvSig, clientEmail) => {
    if ((signQuote || savedQuote)?.stock_order) { toast.error(tr("Commande stock : une connexion est nécessaire pour la signer.")); return; }
    const email = (clientEmail || client.email || "").trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { toast.error(tr("E-mail du client obligatoire pour signer (copie du devis signé).")); return; }
    ensureDraftId();
    const payload = buildPayload();
    if (!payload.vehicle_source) { toast.error(tr("Indiquez si le véhicule est en stock ou à fabriquer avant de passer commande.")); return; }
    const at = new Date().toISOString();
    const sign = { signature, signed_at: at, cgv_accepted_at: at, cgv_signature: cgvSig || "", client_email: email };
    payload.client = { ...payload.client, email };
    markPending(payload, sign);
    lockedRef.current = true;
    localStorage.removeItem("fautras_draft_id");
    lastSavedRef.current = snapshot(payload);
    const prov = { ...provisionalQuote(payload), ...sign };
    setOfflineSaved(prov);
    setSignature(signature); setCgvAccepted(true); setSignOpen(false);
    toast.success(tr("Commande signée sur l'appareil (hors ligne)"), {
      id: "offline-signed", duration: 15000,
      description: tr("Elle sera enregistrée, numérotée et envoyée par e-mail dès le retour de la connexion."),
      action: { label: tr("PDF provisoire"), onClick: () => openProvisionalPdf(prov) },
    });
  };
  const signAndOrder = async (sig, cgvSig, clientEmail) => {
    const signature = sig;
    if (!signature) { toast.error(tr("Veuillez faire signer le client (Bon pour accord) avant de passer commande.")); return; }
    if (!requireColor()) return;
    if (navigator.onLine === false) { signOffline(signature, cgvSig, clientEmail); return; }
    const q0 = signQuote || savedQuote || (await saveQuote());
    const q = q0 && (await ensureSource(q0).catch(() => q0));
    if (!q) return;
    setSignature(signature); setCgvAccepted(true); setSaving(true);
    const t = toast.loading("Signature et envoi de la commande…");
    try {
      const cgvAt = new Date().toISOString();
      signedRef.current = false;
      await api.put(`/quotes/${q.id}/signature`, { signature, signed_at: cgvAt, cgv_accepted_at: cgvAt, cgv_signature: cgvSig || "", client_email: clientEmail || "" });
      signedRef.current = true;
      lockedRef.current = true;
      if (!q.stock_order) localStorage.removeItem("fautras_draft_id");
      if (q.stock_order) { stockDraftRef.current = ""; await addStockOrderExpo(q); }
      if (clientEmail && !q.stock_order) setClient((c) => ({ ...c, email: clientEmail }));
      const data = await sendQuotePdfEmail(q.id);
      toast.success(`Commande confirmée — devis envoyé à ${data.count} destinataire(s)`, { id: t });
      if (!q.stock_order) setSavedQuote({ ...q, status: data.status || "Confirmé", signature, cgv_accepted_at: cgvAt });
      setSignOpen(false);
      setOrderPdf(data.pdf);
    } catch (err) {
      if (!err.response && !(q.signature || "").trim() && !signedRef.current) { toast.dismiss(t); signOffline(signature, cgvSig, clientEmail); return; }
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message, { id: t });
    } finally { setSaving(false); }
  };

  const paperOrder = async (files) => {
    if (!requireColor()) return;
    const q0 = signQuote || savedQuote || (await saveQuote());
    const q = q0 && (await ensureSource(q0).catch(() => q0));
    if (!q) return;
    setSaving(true);
    const tid = toast.loading(tr("Enregistrement de la commande papier…"));
    try {
      const { data: sq } = await api.put(`/quotes/${q.id}/paper-order`, { files });
      setSignature(sq.signature); setSavedQuote({ ...q, ...sq });
      const data = await sendQuotePdfEmail(q.id).catch((err) => {
        setSignOpen(false);
        throw Object.assign(err, { message: tr("Commande papier enregistrée, mais l'e-mail n'est pas parti : renvoyez-la depuis Mes devis avec le bouton e-mail."), response: undefined });
      });
      toast.success(`Commande papier enregistrée — envoyée à ${data.count} destinataire(s)`, { id: tid });
      setSignature(sq.signature); setSavedQuote({ ...q, ...sq, status: data.status || "Confirmé" });
      setSignOpen(false);
      setOrderPdf({ ...data.pdf, paper: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message, { id: tid });
    } finally { setSaving(false); }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {centerMsg && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center pointer-events-none px-4">
          <div className="pointer-events-auto bg-destructive text-white rounded-2xl shadow-2xl px-8 py-6 text-xl sm:text-2xl font-semibold text-center animate-in fade-in zoom-in-95"
            onClick={() => setCenterMsg("")} data-testid="center-alert">{centerMsg}</div>
        </div>
      )}
      {modelPromos.length > 0 && (
        <div className="sticky top-16 z-30 -mx-4 sm:-mx-6 lg:-mx-8 mb-4 px-4 sm:px-6 lg:px-8 py-2.5 bg-background/95 backdrop-blur border-b border-primary/30" data-testid="promos-section">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="inline-flex items-center gap-1.5 font-semibold text-sm shrink-0"><Tag className="w-4 h-4 text-primary" /> {t("Promotions / remises salon :")}</span>
            {modelPromos.map((p) => (
              <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer rounded-lg border border-primary/40 bg-primary/5 px-3 py-1.5" data-testid={`promo-${p.id}`}>
                <Checkbox checked={activePromoIds.has(p.id)}
                  onCheckedChange={(v) => setActivePromoIds((prev) => {
                    if (v && p.exclusive) return new Set([p.id]);
                    const nx = new Set([...prev].filter((id) => !(v && modelPromos.find((x) => x.id === id)?.exclusive)));
                    if (v) nx.add(p.id); else nx.delete(p.id);
                    return nx;
                  })}
                  data-testid={`promo-check-${p.id}`} />
                <span className="font-medium">{p.name}</span>
                {!(p.country_codes || []).some((c) => c.toUpperCase() === String(countryCode).toUpperCase()) && (
                  <span className="text-[10px] font-mono rounded bg-amber-100 text-amber-800 px-1.5 py-0.5" data-testid={`promo-country-${p.id}`}>{(p.country_codes || []).join(" · ")}</span>)}
                {(p.start_date || p.end_date) && (<span className="text-xs text-muted-foreground">({p.start_date ? fmtFR(p.start_date) : "…"} → {p.end_date ? fmtFR(p.end_date) : "…"})</span>)}
              </label>
            ))}
          </div>
        </div>
      )}
      {/* Back to selection */}
      <div className="flex items-center justify-between gap-3 mb-4">
        <Link to="/selection" data-testid="back-to-selection"
          className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5">
          <ArrowLeft className="w-4 h-4" /> {t("Choisir un modèle")}
        </Link>
      </div>

      {/* Range tabs + model selector */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
        <div className="inline-flex rounded-lg border border-border bg-card p-1">
          {ranges.map((r) => (
            <button
              key={r.key}
              data-testid={`range-${r.key}`}
              onClick={() => { setRangeKey(r.key); setGroup(null); }}
              className={`px-4 py-2 text-sm font-semibold rounded-md transition-colors ${
                rangeKey === r.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {t(r.name)}
            </button>
          ))}
        </div>
        <div className="sm:ml-auto w-full sm:w-96 flex gap-2">
          {rangeKey === "occasion" && (
            <Button variant="outline" className="shrink-0 gap-1.5" onClick={() => { setDraftSeed({ key: Date.now(), used: {} }); setCatalog((c) => ({ ...c, models: c.models.map((m) => (m.id === USED_DRAFT_ID ? usedDraftModel() : m)) })); setModelId(USED_DRAFT_ID); }} data-testid="used-add">
              <Plus className="w-4 h-4" /> {t("Ajouter")}
            </Button>
          )}
          <Select value={modelId || ""} onValueChange={setModelId}>
            <SelectTrigger data-testid="model-select"><SelectValue placeholder={rangeKey === "occasion" && !visibleModels.length ? t("Aucune occasion en stock") : t("Choisir un modèle")} /></SelectTrigger>
            <SelectContent className="max-h-80">
              {visibleModels.map((m) => (
                <SelectItem key={m.id} value={m.id}>{t(stripParen(m.name))}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Hero model photo */}
      {model && (
        <motion.div
          key={model.id}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="rounded-2xl overflow-hidden border border-border mb-8 flex flex-col sm:flex-row"
        >
          {model.image && (
            <div className="bg-white shrink-0 flex items-center justify-center self-start">
              <img src={model.image} alt={model.name} className="h-40 sm:h-52 w-auto object-contain" />
            </div>
          )}
          <div className="flex-1 p-5 sm:p-6 flex flex-col justify-center border-t sm:border-t-0 sm:border-l border-border">
            <div className="text-[10px] font-bold uppercase tracking-widest text-primary">{t(currentRange?.name)}{model.category ? ` · ${t(model.category)}` : ""}</div>
            <div className="flex flex-wrap items-center justify-between gap-4 mt-1">
              <h1 className="font-display text-2xl sm:text-4xl font-extrabold flex items-center gap-2 uppercase">{t(stripParen(model.name))}<ModelInfo name={stripParen(model.name)} details={model.details || extractParen(model.name)} className="text-muted-foreground hover:text-foreground" /></h1>
              <div className="w-full sm:w-72">
                <div className="flex items-center gap-1.5 mb-1 text-xs font-semibold"><MapPin className="w-3.5 h-3.5 text-primary" /> {t("Pays de livraison")}</div>
                <Select value={countryCode} onValueChange={(v) => { setCountryCode(v); localStorage.setItem("fautras_country", v); }}>
                  <SelectTrigger data-testid="country-select" className="w-full"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {countries.filter((c) => !pricing?.allowed || pricing.allowed.includes(c.code)).map((c) => (
                      <SelectItem key={c.code} value={c.code}>{t(c.name)} · {t("TVA")} {Math.round(vatFor(c.code) * 1000) / 10}%</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {pricing && pricing.currency !== "EUR" && <div className="text-[11px] text-muted-foreground mt-1" data-testid="fx-info">{t("Prix en")} {eurView ? "EUR" : pricing.currency} · 1 € = {pricing.rate} {pricing.currency}{pricing.rate_date ? ` (${t("cours BCE du")} ${pricing.rate_date})` : ""}</div>}
                {pricing && pricing.currency !== "EUR" && (
                  <div className="inline-flex mt-1.5 rounded-full border p-0.5 text-[11px]" data-testid="currency-toggle">
                    {[[false, pricing.currency], [true, "EUR €"]].map(([v, l]) => (
                      <button key={l} type="button" onClick={() => { setShowEur(v); localStorage.setItem("fautras_show_eur", String(v)); }}
                        className={`px-2.5 py-0.5 rounded-full transition-colors ${showEur === v ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                        data-testid={`currency-toggle-${v ? "eur" : "local"}`}>{l}</button>
                    ))}
                  </div>
                )}
                {eurView && <div className="text-[11px] text-muted-foreground mt-1" data-testid="eur-view-note">{t("Affichage indicatif en euros : le devis et le PDF restent en")} {pricing.currency}</div>}
                {pricing && !pricing.available && <div className="text-[11px] text-destructive mt-1" data-testid="no-price-info">{t("Pas encore de tarif pour ce pays : prix France affichés")}</div>}
              </div>
            </div>
            <button type="button" onClick={() => setHtOnly((v) => !v)} aria-pressed={htOnly} data-testid="ht-only-toggle-top"
              className={`mt-3 self-start w-fit inline-flex items-center rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${htOnly ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary hover:text-primary"}`}>
              {htOnly ? t("Prix HT uniquement · afficher aussi le TTC") : t("Afficher uniquement les prix HT")}
            </button>

            <p className="text-[11px] text-muted-foreground mt-2">{rangeKey === "occasion" ? <span data-testid="used-line">{usedLine(model.used || {})} · {model.used?.vat_recoverable ? t("TVA récupérable (20 %)") : t("Sans TVA")}<br /><span className="text-[#9E7B41] font-medium" data-testid="used-warranty-line">{warrantyLine(model.used || {})}</span></span> : t("Photo du modèle à titre indicatif (peut comporter des options payantes).")}{rangeKey === "occasion" ? "" : rangeKey === "van"
              ? (!["FR", "CH"].includes(pricing?.country || countryCode) ? ` ${t("Prix hors transport usine → revendeur.")}` : ` ${t("Le prix public inclut la livraison au revendeur le plus proche dans le pays sélectionné.")}`)
              : (countryCode !== "FR" ? ` ${t("Prix revendeur hors transport.")}` : ` ${t("Le prix public inclut le transport usine → revendeur ; prix revendeur hors transport.")}`)}</p>
            {rangeKey === "prosport" && countryCode !== "FR" && (
              <p className="text-xs text-destructive font-medium mt-1" data-testid="prosport-no-public">{t("Pas encore de prix public hors France pour cette gamme : les prix France sont utilisés.")}</p>
            )}
          </div>
        </motion.div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:gap-8 items-start">
        {/* Left: options */}
        <div className="space-y-4">
          {rangeKey === "occasion" && (
            <div className="rounded-xl border border-border bg-card p-4 space-y-3" data-testid="used-card">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="font-display text-xl font-bold">{modelId === USED_DRAFT_ID || !model ? t("Nouvelle occasion") : t("Descriptif")}</h2>
                <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setImportOpen(true)} data-testid="used-import-tradein">
                  <Download className="w-3.5 h-3.5" /> {t("Importer une reprise d'un véhicule d'occasion")}
                </Button>
              </div>
              <ImportTradeInDialog open={importOpen} onOpenChange={setImportOpen} onPick={importTradeIn} />
              {modelId === USED_DRAFT_ID || !model ? (
                <UsedVanInline key={draftSeed.key} existing={null} initial={draftSeed.used} showSave={false} onChange={patchUsedModel} />
              ) : (user?.role === "admin" || model.created_by === user?.id) ? (
                usedExpo ? <UsedVanInline key={`${usedExpo.id}-${usedExpo.updated_at || ""}`} existing={usedExpo} onChange={patchUsedModel} onSaved={(d) => { setUsedExpo(d); patchUsedModel(d.used || {}); }} />
                  : <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              ) : (
                <>
                  {(model.used?.options_text || "").trim()
                    ? <ul className="text-sm list-disc pl-5 space-y-0.5" data-testid="used-options-list">{model.used.options_text.split(/\n+/).filter((x) => x.trim()).map((x, i) => <li key={i}>{x.trim()}</li>)}</ul>
                    : <p className="text-sm text-muted-foreground">{t("Aucune option renseignée")}</p>}
                  {(model.used?.photos || []).length > 1 && (
                    <div className="flex flex-wrap gap-2" data-testid="used-photos">
                      {model.used.photos.map((p, i) => <a key={p.url} href={p.url} target="_blank" rel="noreferrer"><img src={p.url} alt={`Photo ${i + 1}`} className="w-24 h-20 object-cover rounded border border-border" /></a>)}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
          {rangeKey !== "occasion" && <h2 className="font-display text-xl font-bold">{t("Mes options")}</h2>}

          {palette.length > 0 && !optionsByCat.STYLE && <ColorPicker colors={palette} value={color} onPick={pickColor} priceOf={colorPrice} ralText={ralText} onRalText={setRalText} extra={colorControls()} />}
          {!optionsByCat.STYLE && stripePicker}

          {/* Option categories */}
          <Accordion type="multiple" value={openCats ?? Object.keys(catsForUi).slice(0, 1)} onValueChange={setOpenCats} className="space-y-2">
            {Object.entries(catsForUi).map(([cat, opts]) => {
              const count = opts.filter((o) => selected[o.id]).length;
              return (
                <AccordionItem key={cat} value={cat} className="border border-border rounded-xl bg-card px-4">
                  <AccordionTrigger className="hover:no-underline" data-testid={`optcat-${cat}`}>
                    <span className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide">
                      <span className={`w-2 h-2 rounded-full ${count > 0 ? "bg-primary" : "bg-border"}`} />
                      {t(cat)}
                      {count > 0 && <span className="text-primary">({count})</span>}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent>
                    {cat === "STYLE" && (palette.length > 0 || stripePicker) && <div className="pb-3">{palette.length > 0 && <ColorPicker colors={palette} value={color} onPick={pickColor} priceOf={colorPrice} ralText={ralText} onRalText={setRalText} extra={colorControls()} />}{stripePicker}</div>}
                    {cat === ptacCat && ptacList.length > 0 && <PtacPicker values={ptacList} value={ptacEff} onChange={setPtac} t={t} />}
                    <div className="divide-y divide-border">
                      {opts.filter((o) => !(palette.length > 0 && COLOR_OPT_RE.test(normName(o.name)))).map((o) => {
                        const isColorOpt = false;
                        const included = (o.public_ht || 0) <= 0;
                        const entry = selected[o.id];
                        const qty = entry?.qty || 0;
                        const disc = entry?.disc || 0;
                        const hasOverride = entry?.priceTtc != null && entry?.priceTtc !== "";
                        const pPub = promoMaps.optPub[o.id] || 0;
                        const effDisc = disc > 0 ? disc : pPub;
                        const catUnitTtc = priceTtcOf(o, totals.vat);
                        const effUnitTtc = hasOverride ? Number(entry.priceTtc) : catUnitTtc * (1 - effDisc / 100);
                        const lineTtc = effUnitTtc * qty;
                        return (
                          <div key={o.id} data-testid={`option-${o.id}`}
                            className="py-3 flex flex-wrap sm:flex-nowrap items-center gap-x-3 gap-y-2">
                            {o.image ? (
                              <img src={o.image} alt={o.name} className="w-12 h-12 rounded-md object-cover border border-border shrink-0 bg-white" data-testid={`option-img-${o.id}`} />
                            ) : (
                              <div className="w-12 h-12 rounded-md border border-dashed border-border shrink-0" />
                            )}
                            <div className="min-w-0 flex-1 basis-[calc(100%-4rem)] sm:basis-auto">
                              <div className="text-sm leading-snug flex items-center gap-1.5">{t(o.name)}<OptionInfo image={o.image} description={o.details} name={o.name} /></div>
                              <div className="text-xs text-muted-foreground mt-0.5">
                                {included ? t("Inclus") : `${t("Prix unitaire :")} ${euroInt(priceTtcOf(o, totals.vat))} ${t("TTC")}${(o.max_qty ?? 1) > 1 ? ` · max ${o.max_qty}` : ""}`}
                              </div>
                              {qty > 0 && isPlaceOpt(o) && <PlacePicker opt={o} place={placeOf(o, qty, entry?.place)} onChange={setPlace} />}
                              {qty > 0 && /trailer cam/i.test(o.name) && (
                                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5" data-testid={`broches-${o.id}`}>
                                  {["13", "7"].map((b) => (
                                    <label key={b} className="flex items-center gap-1.5 text-xs cursor-pointer" data-testid={`broches-${o.id}-${b}`}>
                                      <Checkbox checked={(entry?.broches || "13") === b} onCheckedChange={(v) => v && setBroches(o, b)} /> {t("Véhicule tracteur en")} {b} {t("broches")}
                                    </label>
                                  ))}
                                </div>
                              )}
                            </div>
                            {included ? (
                              <span className="text-xs font-semibold text-[#c9a227] whitespace-nowrap px-2">{t("Inclus")}</span>
                            ) : (
                              <>
                                {isColorOpt ? (
                                  <div className="w-16 h-8 flex items-center justify-center" title={`Couleur : ${color}`}>
                                    <span className="w-5 h-5 rounded border border-border" style={{ background: /ral/i.test(color) ? "repeating-linear-gradient(45deg, #ffffff 0 4px, #d32f2f 4px 6px)" : palette.find((c) => c.name === color)?.hex }} data-testid="color-option-swatch" />
                                  </div>
                                ) : (o.max_qty ?? 1) === 1 ? (
                                  <div className="w-16 h-8 flex items-center justify-center">
                                    <Checkbox checked={qty > 0} onCheckedChange={(v) => setQty(o, v ? 1 : 0)} className="w-5 h-5" data-testid={`option-check-${o.id}`} aria-label={`Sélectionner ${o.name}`} />
                                  </div>
                                ) : (
                                <Select value={String(qty)} onValueChange={(v) => setQty(o, Number(v))}>
                                  <SelectTrigger className="w-16 h-8" data-testid={`option-qty-${o.id}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {Array.from({ length: (o.max_qty ?? 1) + 1 }, (_, n) => n).map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                                )}
                                <div className={`flex items-center gap-0.5 ${qty === 0 ? "opacity-40" : ""}`} title="Remise sur cette option (%)">
                                  <PctInput disabled={qty === 0}
                                    value={qty > 0 ? (disc > 0 ? disc : (pPub || "")) : ""} placeholder="0"
                                    onChange={(v) => setDisc(o, v)} data-testid={`option-disc-${o.id}`}
                                    className="w-12 h-8 text-xs font-mono px-1.5 text-right" />
                                  <span className="text-xs text-muted-foreground">%</span>
                                </div>
                                <div className={`flex items-center gap-1 ${qty === 0 ? "opacity-40" : ""}`} title="Prix unitaire TTC (modifiable)">
                                  <AmountInput disabled={qty === 0}
                                      value={qty > 0 ? (hasOverride ? entry.priceTtc : Math.round(effUnitTtc)) : ""}
                                    placeholder={String(Math.round(catUnitTtc * displayCurrency().rate))}
                                    onChange={(v) => setUnitPrice(o, v)} data-testid={`option-price-${o.id}`}
                                    className="w-20 h-8 text-xs font-mono px-1.5 text-right" />
                                  <span className="text-xs text-muted-foreground">{sym}</span>
                                </div>
                                <span className="w-24 text-right whitespace-nowrap">
                                  {qty > 0 && effUnitTtc < catUnitTtc && (
                                    <span className="block text-[10px] text-muted-foreground line-through leading-none">{euro(catUnitTtc * qty)}</span>
                                  )}
                                  <span className="font-mono text-sm" data-testid={`option-total-${o.id}`}>
                                    {qty > 0 ? euro(lineTtc) : euro(0)}
                                  </span>
                                </span>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
            {rangeKey !== "occasion" && <AccordionItem value="__special__" className="border border-border rounded-xl bg-card px-4" data-testid="special-options">
              <AccordionTrigger className="hover:no-underline" data-testid="optcat-special">
                <span className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide">
                  <span className={`w-2 h-2 rounded-full ${specials.some((s) => s.name || (Number(s.priceTtc) || 0) > 0) ? "bg-primary" : "bg-border"}`} />
                  {t("Options spéciales")}
                  {specials.filter((s) => s.name || (Number(s.priceTtc) || 0) > 0).length > 0 && <span className="text-primary">({specials.filter((s) => s.name || (Number(s.priceTtc) || 0) > 0).length})</span>}
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-3 pt-1">
                  <p className="text-xs text-muted-foreground">{t("Options sur-mesure avec description et pièce jointe (croquis…).")}</p>
                  {specials.length === 0 && <div className="text-xs text-muted-foreground italic">{t("Aucune option spéciale.")}</div>}
                  <div className="space-y-3">
              {specials.map((s) => (
                <div key={s.id} className="rounded-lg border border-border bg-card p-3 space-y-2" data-testid={`special-${s.id}`}>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    <div>
                      <Label className="text-xs">{t("Prix TTC")} ({sym})</Label>
                      <AmountInput value={s.priceTtc} onChange={(v) => updateSpecial(s.id, { priceTtc: v })} placeholder="0" className="mt-1" data-testid={`special-price-${s.id}`} />
                    </div>
                    <div>
                      <Label className="text-xs">{t("Remise %")}</Label>
                      <PctInput value={s.disc} onChange={(v) => updateSpecial(s.id, { disc: v })} placeholder="0" className="mt-1" data-testid={`special-disc-${s.id}`} />
                    </div>
                    <div>
                      <Label className="text-xs">{t("Prix remisé TTC")}</Label>
                      <div className="mt-1 h-9 flex items-center justify-end px-2 rounded-md bg-muted font-mono text-sm font-semibold" data-testid={`special-net-${s.id}`}>{euroInt(spNet(s))}</div>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">{t("Description")} <span className="text-destructive">*</span>{s.name && <span className="ml-2 font-normal text-muted-foreground" data-testid={`special-autoname-${s.id}`}>{t("Libellé")} : {s.name}</span>}</Label>
                    <Textarea value={s.description} onChange={(e) => updateSpecial(s.id, { description: e.target.value })} rows={2} placeholder={t("Détails de l'option sur-mesure…")} className={`mt-1 ${Number(s.priceTtc) > 0 && !((s.description || "").trim()) ? "border-destructive focus-visible:ring-destructive" : ""}`} data-testid={`special-desc-${s.id}`} />
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <label className="inline-flex items-center gap-1.5 text-xs font-medium cursor-pointer rounded-md border border-border px-2.5 py-1.5 hover:bg-accent" data-testid={`special-attach-label-${s.id}`}>
                      {s.uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />}
                      {t(s.attachmentName ? "Remplacer la pièce jointe" : "Ajouter une pièce jointe")}
                      <input type="file" className="hidden" onChange={(e) => uploadSpecial(s.id, e.target.files?.[0])} data-testid={`special-file-${s.id}`} />
                    </label>
                    {s.attachmentName && (
                      <a href={s.attachmentUrl} target="_blank" rel="noreferrer" className="text-xs text-primary underline inline-flex items-center gap-1 truncate max-w-[200px]" data-testid={`special-attach-link-${s.id}`}>{s.attachmentName}</a>
                    )}
                    <Button type="button" size="sm" variant="ghost" onClick={() => removeSpecial(s.id)} className="ml-auto text-destructive hover:text-destructive gap-1" data-testid={`special-remove-${s.id}`}><Trash2 className="w-3.5 h-3.5" /> {t("Retirer")}</Button>
                  </div>
                </div>
              ))}
                  </div>
                  <div className="flex justify-end">
                    <Button type="button" size="sm" variant="outline" onClick={addSpecial} data-testid="add-special-option" className="gap-1.5"><Plus className="w-4 h-4" /> {t("Ajouter une option spéciale")}</Button>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>}
            <AccordionItem value="__tradein__" className="border border-border rounded-xl bg-card px-4" data-testid="tradein-section">
              <AccordionTrigger className="hover:no-underline" data-testid="optcat-tradein">
                <span className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide">
                  <span className={`w-2 h-2 rounded-full ${tradeInEnabled ? "bg-primary" : "bg-border"}`} />
                  {t("Reprise d'un véhicule d'occasion")}
                  {tradeInEnabled && <span className="text-primary">({filledTradeIns.length})</span>}
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-3 pt-1">
                  {tradeIns.map((ti, i) => {
                    const montant = Number(ti.montant) || 0;
                    const htVal = montant / (ti.tvaRecup ? (1 + totals.vat) : 1);
                    const priceEditable = !!ti.htp || (montant > 0 && !!(ti.marque || "").trim());
                    const showCrit = !!ti.htp || !!(ti.marque || "").trim();
                    return (
                      <div key={i} className="rounded-lg border border-border p-3 space-y-2" data-testid={`reprise-card-${i}`}>
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <span className="text-xs font-semibold text-primary">{t("Reprise")} {i + 1}</span>
                          {showCrit && <fieldset disabled className="flex flex-wrap items-center gap-x-4 gap-y-1.5 opacity-90" data-testid={`reprise-type-${i}`}>
                            <span className="text-[11px] text-muted-foreground">{t("Type")} :</span>
                            {[["van", "Van chevaux"], ["fourgon", "Remorque fourgon"], ["autre", "Autre"]].filter(([val]) => htpCrit.types.includes(val) || ti.type === val).map(([val, lbl]) => (
                              <label key={val} className="flex items-center gap-1.5 text-xs cursor-pointer" data-testid={`reprise-${i}-type-${val}`}>
                                <Checkbox checked={ti.type === val} onCheckedChange={(v) => { if (v) setReprise(i, "type", val); }} />
                                {t(lbl)}
                              </label>
                            ))}
                          </fieldset>}
                          <Button type="button" size="sm" className="gap-1" onClick={() => estimateReprise(i)} data-testid={`reprise-estimate-${i}`}>
                            <ExternalLink className="w-3.5 h-3.5" /> {t("Estimer la reprise")}
                          </Button>
                          {(ti.marque || "").trim() && (
                            <Button type="button" size="sm" variant="outline" className="gap-1" data-testid={`reprise-to-stock-${i}`}
                              onClick={() => setUsedDlg({ initial: { type: ti.type || "van", brand: ti.marque || "", brand_other: !!ti.marqueAutre, model: ti.modele || "", places: ti.type === "van" ? (ti.places || "") : "", year: ti.annee || "", plate: ti.immat || "", ptac: ti.ptac || "", floor_type: ti.plancher_type || "", vat_recoverable: !!ti.tvaRecup, photos: ti.photos || [], ...((savedQuote?.id || expoConfig?._quote?.id) && filledTradeIns.includes(ti) ? { source_tradein: { quote_id: savedQuote?.id || expoConfig._quote.id, index: filledTradeIns.indexOf(ti) } } : {}), ...(Number(ti.montant) > 0 ? { cost_ht: String(Math.round(Number(ti.montant) / (ti.tvaRecup ? 1 + totals.vat : 1) * 100) / 100) } : {}) } })}>
                              <Store className="w-3.5 h-3.5" /> {t("Mettre en stock occasion")}
                            </Button>
                          )}
                          {(tradeIns.length > 1 || filledTradeIns.includes(ti)) && (
                            <Button type="button" size="sm" variant="ghost" className="ml-auto text-destructive gap-1" onClick={() => removeReprise(i)} data-testid={`reprise-remove-${i}`}>
                              <Trash2 className="w-3.5 h-3.5" /> {t("Supprimer la reprise")}
                            </Button>
                          )}
                        </div>
                        {!ti.htp && <p className="text-xs text-primary font-medium" data-testid={`reprise-${i}-estimate-hint`}>{t("Cliquez sur « Estimer la reprise » : les informations du véhicule et le prix de reprise sont remplis par l'estimateur.")}</p>}
                        {showCrit && <fieldset disabled className="min-w-0" data-testid={`reprise-${i}-readonly`}>
                          <RepriseHtpFields ti={ti} i={i} crit={htpCrit} setReprise={setReprise} patchReprise={patchReprise} />
                        </fieldset>}
                        {(ti.photos || []).length > 0 && <div className="flex flex-wrap items-center gap-2" data-testid={`reprise-${i}-photos`}>
                          {(ti.photos || []).map((p, pi) => (
                            <a key={`${p.url}-${pi}`} href={p.url} target="_blank" rel="noreferrer"><img src={p.url} alt={p.name} className="w-16 h-12 object-cover rounded border border-border" /></a>
                          ))}
                        </div>}
                        {showCrit && <div className="flex items-center justify-end gap-4 flex-wrap pt-2 border-t border-border">
                          {ti.tvaRecup && <label className="flex items-center gap-1.5 text-xs whitespace-nowrap opacity-90" data-testid={`reprise-${i}-tvarecup-label`}>
                            <Checkbox checked disabled data-testid={`reprise-${i}-tvarecup`} />
                            <span>{t("TVA récupérable")}</span>
                          </label>}
                          <div className="flex items-center gap-2">
                            <Label className="text-muted-foreground text-xs">{t("Reprise")} ({sym} {t("TTC")}) <span className="text-destructive">*</span></Label>
                            <AmountInput value={ti.montant || ""} placeholder="0" disabled={!priceEditable}
                              onChange={(v) => setReprise(i, "montant", Math.max(0, Number(v) || 0))}
                              data-testid={`reprise-${i}-montant`} className="w-28 h-8 text-right font-mono" />
                          </div>
                          {ti.tvaRecup && <div className="flex items-center gap-2">
                            <Label className="text-muted-foreground text-xs">{t("Reprise")} ({sym} {t("HT")})</Label>
                            <AmountInput value={htVal ? Math.round(htVal * 100) / 100 : ""} placeholder="0" disabled={!priceEditable}
                              onChange={(v) => setReprise(i, "montant", Math.max(0, (Number(v) || 0) * (ti.tvaRecup ? (1 + totals.vat) : 1)))}
                              data-testid={`reprise-${i}-montant-ht`} className="w-28 h-8 text-right font-mono" />
                          </div>}
                        </div>}
                      </div>
                    );
                  })}
                  <div className="flex justify-end">
                    <Button type="button" size="sm" variant="outline" className="gap-1.5" onClick={addReprise} data-testid="tradein-add-btn">
                      <Plus className="w-4 h-4" /> {t("Ajouter une reprise")}
                    </Button>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>

        {/* Right: price box */}
        <div className="w-full">
          <div className="rounded-2xl border border-border bg-card overflow-hidden">
            <div className="p-5 border-b border-border">
              <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{t("Votre configuration")}</p>
              <div className="mt-1 font-display font-bold leading-snug flex items-start gap-1.5">{model ? t(stripParen(model.name)) : "—"}{model && <ModelInfo name={stripParen(model.name)} details={model.details || extractParen(model.name)} className="mt-0.5" />}</div>
              {color && <div className="text-xs text-muted-foreground mt-0.5" data-testid="summary-color">{t("Couleur")} : {t(colorLabel)}</div>}
              {stripeLabel && <div className="text-xs text-muted-foreground mt-0.5" data-testid="summary-stripe">{t("Bandes déco")} : {stripeLabel}</div>}
              {archLabel && <div className="text-xs text-muted-foreground mt-0.5" data-testid="summary-arch">{t("Arches")} : {archLabel}</div>}
            </div>

            <div className="p-5 space-y-2.5 text-sm">
              <button type="button" onClick={() => setHtOnly((v) => !v)} aria-pressed={htOnly} data-testid="ht-only-toggle"
                className={`w-full rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${htOnly ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary hover:text-primary"}`}>
                {htOnly ? t("Prix HT uniquement · afficher aussi le TTC") : t("Afficher uniquement les prix HT")}
              </button>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t("Prix du modèle")}</span>
                <span className="font-mono" data-testid="model-price-before">{euroInt(vh(totals.catModelTtc))}</span>
              </div>
              <div className="space-y-1" data-testid="options-lines">
                {Object.values(selected).filter((e) => e.qty > 0).map((e) => (
                  <div key={e.opt.id} className="flex justify-between gap-3 text-xs" data-testid={`option-line-${e.opt.id}`}>
                    <span className="text-muted-foreground min-w-0">+ {t(rangeKey === "van" ? stripParen(e.opt.name) : e.opt.name)}{e.qty > 1 ? ` (x${e.qty})` : ""}</span>
                    <span className="font-mono shrink-0">{euroInt(vh(priceTtcOf(e.opt, totals.vat) * e.qty))}</span>
                  </div>
                ))}
                {specials.filter((s) => s.name && Number(s.priceTtc) > 0).map((s) => (
                  <div key={s.id} className="flex justify-between gap-3 text-xs" data-testid={`special-line-${s.id}`}>
                    <span className="text-muted-foreground min-w-0">+ {s.name}</span>
                    <span className="font-mono shrink-0">{euroInt(vh(Number(s.priceTtc)))}{spDisc(s) > 0 && <span className="text-primary"> → {euroInt(vh(spNet(s)))}</span>}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-border pt-2.5 flex justify-between text-lg font-bold">
                <span>{t(aidMode ? "Sous-total avant aide" : "Sous-total avant remise")}</span>
                <span className="font-mono" data-testid="subtotal-before">{euroInt(vh(totals.subBeforeTtc))}</span>
              </div>

              <div className="flex items-center justify-between gap-2 pt-1">
                <span className="text-muted-foreground">{t(aidMode ? "Aide totale client" : "Remise totale client")}</span>
                <div className="flex items-center gap-1">
                  <Input type="number" min={0} max={100} step="0.1" title="Remise en % sur le sous-total avant remise"
                    value={finalPct ?? String(totals.subBeforeTtc > 0 ? Math.round(((totals.subBeforeTtc - totals.totalAfterTtc) / totals.subBeforeTtc) * 1000) / 10 : 0)}
                    onFocus={(e) => { pctBaseRef.current = { mod: totals.modelAfterTtc, opt: totals.optionsAfterTtc }; setFinalPct(e.target.value); }}
                    onBlur={() => { setFinalPct(null); pctBaseRef.current = null; }}
                    onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (!pctBaseRef.current) pctBaseRef.current = { mod: totals.modelAfterTtc, opt: totals.optionsAfterTtc };
                      setFinalPct(v);
                      if (v === "") return;
                      const p = Math.min(100, Math.max(0, Number(v) || 0));
                      applyTotal(Math.round(totals.subBeforeTtc * (1 - p / 100)), pctBaseRef.current);
                    }} data-testid="final-pct-input" className="w-16 h-8 text-right font-mono" />
                  <span className="text-xs text-muted-foreground mr-1">%</span>
                  <span className="font-mono text-[#c9a227] w-28 text-right" data-testid="client-discount">{totals.clientDiscount > 0.5 ? `- ${euro(vh(totals.clientDiscount))}` : euro(0)}</span>
                </div>
              </div>
              {!htOnly && <div className="flex justify-between border-t border-border pt-2.5">
                <span className="text-muted-foreground">{t(aidMode ? "Total après aide HT" : "Total après remise HT")}</span>
                <span className="font-mono" data-testid="total-ht">{euro(totals.totPubHt)}</span>
              </div>}
              <div className={`flex items-center justify-between gap-2 ${htOnly ? "border-t border-border pt-2.5" : ""}`}>
                <span className="font-display font-bold text-xl">{htOnly ? t(aidMode ? "Total après aide HT" : "Total après remise HT") : t(aidMode ? "Total après aide TTC" : "Total après remise TTC")}</span>
                <div className="flex items-center gap-1">
                  <Input type="number" min={0} value={totalDraft ?? String(Math.round(vh(totals.totalAfterTtc) * displayCurrency().rate))}
                    onChange={(e) => setTotalDraft(e.target.value)}
                    onBlur={() => { if (totalDraft != null && totalDraft !== "") { applyTotal(Number(totalDraft) / displayCurrency().rate * (htOnly ? vatK : 1)); setFinalPct(null); pctBaseRef.current = null; } setTotalDraft(null); }}
                    onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }} data-testid="total-after-input"
                    className="w-36 h-11 text-right font-mono text-xl font-bold" />
                  <span className="text-xs text-muted-foreground">{sym}</span>
                </div>
              </div>

              {regOn && (
                <div className="space-y-1 border-t border-border pt-2" data-testid="reg-fees-block">
                  {regOpen && regGroups.map((g) => {
                    const on = g.keys.some((k) => regKeys.includes(k));
                    return (
                      <label key={g.id} className="flex items-center justify-between text-sm cursor-pointer" data-testid={`reg-fee-${g.id}`}>
                        <span className="flex items-center gap-2">
                          <Checkbox checked={on} disabled={regLocked} onCheckedChange={(v) => setRegSel(v ? Array.from(new Set([...regKeys, ...g.keys])) : regKeys.filter((k) => !g.keys.includes(k)))} data-testid={`reg-fee-check-${g.id}`} />
                          <span className="text-muted-foreground">{t(htOnly ? g.label.replace(/ TTC$/, " HT") : g.label)}{g.noVat ? ` (${t("sans TVA")})` : ""}</span>
                        </span>
                        <span className={`font-mono ${on && !(regFullTtc > 0 && regTtc <= 0.004) ? "" : "text-muted-foreground line-through"}`} data-testid={`reg-fee-amount-${g.id}`}>{euro((on ? g.keys.filter((k) => regKeys.includes(k)) : g.keys).reduce((s, k) => s + feeAmt(k), 0))}</span>
                      </label>
                    );
                  })}
                  {regFullTtc > 0 && <div className="flex items-center justify-between gap-2 text-sm font-medium" data-testid="reg-total-row">
                    <button type="button" onClick={() => setRegOpen((o) => !o)} className="flex items-center gap-1 text-left hover:text-primary" data-testid="reg-toggle" aria-expanded={regOpen}>
                      <ChevronDown className={`w-4 h-4 transition-transform ${regOpen ? "rotate-180" : ""}`} />{t("Total frais d'immatriculation")}
                    </button>
                    <span className="flex items-center gap-1">
                      <AmountInput value={String(Math.round((regDiscount === "" ? (htOnly ? regFullHt : regFullTtc) : (htOnly ? regHt : regTtc)) * 100) / 100)}
                        onChange={(v) => { if (v === "" || v == null) { setRegDiscount(""); return; } const n = Math.min(Math.max(0, (Number(v) || 0) * (htOnly && regFullHt > 0 ? regFullTtc / regFullHt : 1)), regFullTtc); setRegDiscount(n >= regFullTtc ? "" : String(Math.round((regFullTtc - n) * 100) / 100)); }}
                        className="w-24 h-7 text-right font-mono" data-testid="reg-total-input" />
                      <span className="text-xs text-muted-foreground">{sym}</span>
                    </span>
                  </div>}
                  {(tradeInEnabled && totalTradeIn > 0) && <>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{t("Total avec frais d'immatriculation HT")}</span>
                      <span className="font-mono" data-testid="total-with-reg-ht">{euro(totals.totPubHt + regHt)}</span>
                    </div>
                    {!htOnly && <div className="flex justify-between text-sm font-medium">
                      <span>{t("Total avec frais d'immatriculation TTC")}</span>
                      <span className="font-mono" data-testid="total-with-reg">{euro(totals.totTtc + regTtc)}</span>
                    </div>}
                  </>}
                </div>
              )}
              {tradeInEnabled && totalTradeIn > 0 && (
                <div className="space-y-1 border-t border-border pt-2">
                  {(htOnly || filledTradeIns.some((x) => x.tvaRecup)) && <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t("Prix de reprise HT")}{htOnly && filledTradeIns.length > 1 ? ` (${filledTradeIns.length} ${t("reprises")})` : ""}</span>
                    <span className="font-mono text-destructive" data-testid="tradein-ht">- {euro(repriseHt)}</span>
                  </div>}
                  {!htOnly && <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t("Prix de reprise TTC")}{filledTradeIns.length > 1 ? ` (${filledTradeIns.length} ${t("reprises")})` : ""}</span>
                    <span className="font-mono text-destructive" data-testid="tradein-ttc">- {euro(totalTradeIn)}</span>
                  </div>}
                </div>
              )}
              {!htOnly && <div className="flex items-baseline justify-between text-xs text-muted-foreground pt-1 border-t border-border">
                <span>{t(tradeInEnabled && totalTradeIn > 0 ? "Net à payer HT" : regFullTtc > 0 ? "Total avec frais d'immatriculation HT" : "Total à payer HT")}</span>
                <span className="font-mono" data-testid="net-ht">{euro(netHt)}</span>
              </div>}
              <div className={`flex items-baseline justify-between ${htOnly ? "pt-1 border-t border-border" : ""}`}>
                <span className="font-display font-bold text-xl">{t(tradeInEnabled && totalTradeIn > 0 ? `Net à payer ${htOnly ? "HT" : "TTC"}` : regFullTtc > 0 ? `Total avec frais d'immatriculation ${htOnly ? "HT" : "TTC"}` : `Total à payer ${htOnly ? "HT" : "TTC"}`)}</span>
                <span className="font-mono text-2xl sm:text-3xl font-bold text-[#c9a227] whitespace-nowrap" data-testid="net-ttc">{euro(htOnly ? netHt : netTtc)}</span>
              </div>

              <div className="border-t border-border pt-2.5 space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <Label className="text-muted-foreground">{t("Acompte (35%)").replace("35", acomptePct)}{htOnly ? " HT" : ""}</Label>
                  <div className="flex items-center gap-1">
                    <Input type="number" min={0} max={100} step="0.1" value={depPctDraft ?? acomptePct.replace(",", ".")}
                      onFocus={() => setDepPctDraft(acomptePct.replace(",", "."))}
                      onChange={(e) => {
                        const v = e.target.value;
                        setDepPctDraft(v);
                        if (v !== "") setAcompte(String(Math.round(totals.totTtc * Math.min(100, Math.max(0, Number(v) || 0)) / 100)));
                      }}
                      onBlur={() => setDepPctDraft(null)}
                      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                      data-testid="deposit-pct-input" className="w-16 h-8 text-right font-mono" />
                    <span className="text-xs text-muted-foreground mr-1">%</span>
                    <AmountInput value={htOnly ? String(Math.round(acompteValue * payK * 100) / 100) : (acompte !== "" ? acompte : acompteAuto)}
                      onChange={(v) => setAcompte(htOnly && v !== "" && v != null ? String(Math.round(Number(v) / payK * 100) / 100) : v)} data-testid="deposit-input"
                      className="w-28 h-8 text-right font-mono" />
                    <span className="text-xs text-muted-foreground">{sym}</span>
                  </div>
                </div>
                <PayMethods label={t("Acompte payé par") + " :"} methods={["cb", "cheque", "virement"]} value={depositPay} onChange={setDepositPay} testid="deposit-pay" />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{t("Solde à la livraison")}{htOnly ? " HT" : ""}</span>
                  <span className="font-mono" data-testid="deposit-balance">{euro((netTtc - acompteValue) * payK)}</span>
                </div>
                <PayMethods label={t("Solde payé par") + " :"} methods={["cb", "cheque", "virement", "financement"]} value={balancePay} onChange={(v) => { setBalancePay(v); if (!v.includes("financement")) setFinancing(null); }} testid="balance-pay" />
                {countryCode === "FR" && financing && (
                  <FinancingBlock financing={financing} priceTtc={totals.totalAfterTtc} solde={netTtc - acompteValue}
                    onRemove={() => { setFinancing(null); setBalancePay((xs) => xs.filter((m) => m !== "financement")); }} />
                )}
              </div>

              <div className="mt-2 pt-3 border-t border-border text-xs space-y-1 cursor-pointer select-none"
                onMouseDown={() => setShowReseller(true)}
                onMouseUp={() => setShowReseller(false)}
                onMouseLeave={() => setShowReseller(false)}
                onTouchStart={(e) => { e.preventDefault(); setShowReseller(true); }}
                onTouchEnd={() => setShowReseller(false)}
                onContextMenu={(e) => e.preventDefault()}
                data-testid="reseller-toggle"
                title="Maintenir appuyé pour afficher la marge / le coût revendeur">
                <div className="flex items-center justify-end gap-1 text-[10px] text-muted-foreground/70 mb-0.5">
                  {showReseller ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  <span>{t("Maintenir pour afficher marge / coût revendeur")}</span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>{rangeKey === "occasion" ? t("Coût de revient HT") : t("Coût revendeur HT")}</span>
                  <span className="font-mono" data-testid="reseller-cost">{showReseller ? euro(totals.subResHt + regResHt) : "••••••"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t("Marge estimée HT")}</span>
                  <span className={`font-mono ${showReseller && totals.margin + regFullHt - regResHt - regDiscHt < 0 ? "text-destructive" : "text-primary"}`} data-testid="margin-ht">{showReseller ? euro(totals.margin + regFullHt - regResHt - regDiscHt) : "••••••"}</span>
                </div>
              </div>
            </div>

            <div className="p-5 pt-0 space-y-2">
              <div data-testid="delivery-block">
                <Label className="text-xs text-muted-foreground">{t("Lieu de livraison")}</Label>
                <DeliveryPlacePicker value={deliveryPlace} rangeKey={rangeKey === "occasion" ? "" : rangeKey} suggested={deliveryTouched ? "" : deliveryPlace} onChange={(v) => { setDeliveryTouched(true); setDeliveryPlace(v); }} />
                {deliveryHint && !deliveryTouched && <div className="text-[11px] text-muted-foreground mt-0.5" data-testid="delivery-hint">{t("Revendeur du secteur")} : {deliveryHint}</div>}
              </div>
              <div data-testid="delay-block">
                <Label className="text-xs text-muted-foreground">{t("Délai souhaité par le client")}</Label>
                <Input value={deliveryDelay} onChange={(e) => setDeliveryDelay(e.target.value)}
                  placeholder={t("Vide = délai confirmé à réception de commande par Fautras")} data-testid="delivery-delay" className="h-8 text-sm mt-1" />
                {deliveryDelay.trim() && (
                  <label className="flex items-center gap-2 text-xs mt-1.5 cursor-pointer" data-testid="delay-imperative-label">
                    <Checkbox checked={delayImperative} onCheckedChange={(v) => setDelayImperative(!!v)} data-testid="delay-imperative" /> {t("Délai impératif")}
                  </label>
                )}
              </div>
              <VehicleSourcePicker value={vehicleSource || ((expoConfig && !expoConfig._quote) || rangeKey === "occasion" ? "stock" : "")} onChange={setVehicleSource} t={t} />
              {(vehicleSource || ((expoConfig && !expoConfig._quote) || rangeKey === "occasion" ? "stock" : "")) === "stock" && (
                <div data-testid="stock-location-block">
                  <Label className="text-xs text-muted-foreground">{t("Lieu de stockage")}</Label>
                  <DeliveryPlacePicker value={stockLocation} onChange={setStockLocation} testid="stock-location" placeholder="Choisir le lieu de stockage" autoOwn />
                </div>
              )}
              {(vehicleSource || ((expoConfig && !expoConfig._quote) || rangeKey === "occasion" ? "stock" : "")) === "stock" && (
                <div data-testid="serial-block">
                  <Label className="text-xs text-muted-foreground">{t("N° de série")}</Label>
                  <Input value={serialNumber ?? expoSerial ?? ""} onChange={(e) => setSerialNumber(e.target.value)}
                    placeholder={t("À saisir")} data-testid="quote-serial" className="h-8 text-sm mt-1 font-mono" />
                </div>
              )}
              <div data-testid="notes-block">
                <Label className="text-xs text-muted-foreground">{t("Précisions (imprimées sur le devis et la commande)")}</Label>
                <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
                  placeholder={t("Ex. : livraison avec plein d'eau, rendez-vous à confirmer…")} data-testid="quote-notes" className="text-sm mt-1" />
              </div>
              {countryCode === "FR" && (
                <Button variant="outline" className="w-full gap-2" disabled={!model} data-testid="open-financing"
                  onClick={() => {
                    if (!client.name?.trim()) { toast.info("Renseignez d'abord les coordonnées du client"); setFormOpen(true); return; }
                    openFinancing();
                  }}>
                  <Landmark className="w-4 h-4" /> {t("Voir les financements")}
                </Button>
              )}
              {usedNoPrice && <p className="text-xs font-medium text-amber-700" data-testid="used-price-missing">{t("Prix à définir : renseignez le prix de vente de l'occasion avant de faire le devis.")}</p>}
              <Button className="w-full gap-2" disabled={!model || usedNoPrice} onClick={() => {
                if (!checkRequired()) return;
                const go = () => (client.name?.trim() && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test((client.email || "").trim()) ? openSignView() : setFormOpen(true));
                if (!needPhoto(go)) go();
              }} data-testid="receive-quote">
                <Check className="w-4 h-4" /> {t("Voir le récapitulatif du devis")}
              </Button>
              <Button variant="ghost" className="w-full gap-2 text-muted-foreground" disabled={!model} onClick={resetQuote} data-testid="reset-quote">
                <RotateCcw className="w-4 h-4" /> {t("Réinitialiser le devis")}
              </Button>
              {user?.role === "reseller" && rangeKey !== "occasion" && (
                <Button variant="outline" className="w-full gap-2 border-primary text-primary" disabled={!model || saving} onClick={openStockOrder} data-testid="stock-order-btn">
                  <Package className="w-4 h-4" /> {t(isAgent ? "Demander un véhicule de démonstration" : "Commander un véhicule pour son stock")}
                </Button>
              )}
              {canUpdateExpo && (
                <Button className="w-full gap-2" disabled={!model || updatingExpo} onClick={updatePoster} data-testid="update-poster">
                  {updatingExpo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {t("Mettre à jour l'affiche")} « {expoConfig.name} »
                </Button>
              )}
              {rangeKey === "occasion" && (modelId === USED_DRAFT_ID || user?.role === "admin" || model?.created_by === user?.id) && (
                <Button variant="outline" className="w-full gap-2" disabled={!model || usedBusy} onClick={() => setPosterOpen(true)} data-testid="used-stock-poster">
                  {usedBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />} {t("Créer l'affiche de prix et mettre en stock")}
                </Button>
              )}
              {rangeKey !== "occasion" && <Button variant="outline" className="w-full gap-2" disabled={!model} onClick={() => setPosterOpen(true)} data-testid="create-poster">
                <Printer className="w-4 h-4" /> {t("Mettre en stock et créer une affiche prix")}
              </Button>}
              {rangeKey !== "occasion" && <Button variant="outline" className="w-full gap-2 mt-1" disabled={!model} onClick={() => { setCompareId(compareDefault.id); setCompareExtras(compareDefault.extras); setCompareOpen(true); }} data-testid="compare-btn">
                <Scale className="w-4 h-4" /> {t("Comparer avec un autre modèle")}
              </Button>}
            </div>
          </div>
        </div>
      </div>

      {/* Quote form modal (style Fautras) */}
      <UsedVanDialog open={!!usedDlg} onOpenChange={(v) => { if (!v) setUsedDlg(null); }} initial={usedDlg?.initial} existing={usedDlg?.existing}
        onSaved={(d) => {
          if (rangeKey !== "occasion") return;
          reloadUsed(d.id);
        }} />
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("Finaliser le devis")}</DialogTitle>
            <DialogDescription>{t("Renseignez les informations du client pour générer le devis en PDF ou l'envoyer par email.")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <ClientTypeField client={client} t={t} onChange={(p) => setClient((c) => ({ ...c, ...p }))} />
              <div><Label>{t("Nom")} *</Label><Input value={client.name} onChange={(e) => setClient({ ...client, name: e.target.value })} data-testid="client-name" className={`mt-1.5 ${red("nom")}`} /></div>
              <div><Label>{t("Prénom")} *</Label><Input value={client.firstname} onChange={(e) => setClient({ ...client, firstname: e.target.value })} data-testid="client-prenom" className={`mt-1.5 ${red("prénom")}`} /></div>
              <div><Label>{t("Société")}</Label><Input value={client.company} onChange={(e) => setClient({ ...client, company: e.target.value, ...(e.target.value.trim() ? { client_type: "societe" } : {}) })} data-testid="client-company" className="mt-1.5" /></div>
              <div><Label>{t("Téléphone")} *</Label>
                <div className="mt-1.5 flex gap-2">
                  <select value={client.phone_code || "+33"} onChange={(e) => setClient({ ...client, phone_code: e.target.value })} data-testid="client-phonecode" className="h-10 rounded-md border border-input bg-background px-2 text-sm w-24 shrink-0">
                    {PHONE_CODES.map((p) => <option key={p.c} value={p.c}>{p.l} {p.c}</option>)}
                  </select>
                  <Input value={client.phone} onChange={(e) => setClient({ ...client, phone: e.target.value })} data-testid="client-phone" className={`flex-1 ${red("téléphone")}`} />
                </div>
                <PhoneWarn phone={client.phone} code={client.phone_code} testid="client-phone-warn" />
              </div>
              <div><Label>{t("Code postal")} *</Label><Input value={client.postal} onChange={(e) => setClient({ ...client, postal: e.target.value })} data-testid="client-postal" className={`mt-1.5 ${red("code postal")}`} /></div>
              <div><Label>{t("Ville")} *</Label><CityInput value={client.city} onChange={(v) => setClient((c) => ({ ...c, city: v }))} postal={client.postal} countryCode={countryCodeOf(client.country, countryCode)} testid="client-city" className={`mt-1.5 ${red("ville")}`} /></div>
              <div><Label>{t("Pays")}</Label><Input value={client.country} onChange={(e) => setClient({ ...client, country: e.target.value })} data-testid="client-country" className="mt-1.5" /></div>
            </div>
            <div><Label>{t("Adresse")} *</Label><Input value={client.address} onChange={(e) => setClient({ ...client, address: e.target.value })} data-testid="client-address" className={`mt-1.5 ${red("adresse")}`} /></div>
            <div><Label>{t("Email (pour l'envoi du devis)")} *</Label><Input type="email" value={client.email} onChange={(e) => setClient({ ...client, email: e.target.value })} data-testid="client-email" className={`mt-1.5 ${red("email")}`} /></div>
            {missing.some((k) => contactMissing().includes(k)) && <p className="text-xs text-destructive" data-testid="client-missing-msg">{t("Complétez les champs en rouge pour pouvoir envoyer le devis.")}</p>}
            <div className="rounded-lg border border-primary/30 bg-primary/5 px-3 py-2" data-testid="offer-validity">
              <span className="text-sm">{t("Offre valable jusqu'au")} <span className="font-semibold">{fmtFR(offerValidUntil())}</span></span>
            </div>
            {model && (
              <div className="rounded-lg border border-border bg-muted/30 p-3 text-sm" data-testid="quote-recap">
                <div className="font-semibold mb-1">{t("Récapitulatif du devis")}</div>
                <div className="flex justify-between"><span>{t(stripParen(model.name))}</span><span className="font-mono">{euro(vh(totals.catModelTtc))}</span></div>
                {Object.values(selected).filter((e) => e.qty > 0).map((e) => (
                  <div key={e.opt.id} className="flex justify-between text-muted-foreground text-xs"><span>+ {t(e.opt.name)}{e.qty > 1 ? ` ×${e.qty}` : ""}</span></div>
                ))}
                <div className="flex justify-between border-t border-border mt-1 pt-1 font-semibold"><span>{t(htOnly ? "Total HT" : "Total TTC")}</span><span className="font-mono">{euro(vh(totals.totTtc))}</span></div>
                {totalTradeIn > 0 && <div className="flex justify-between text-[#c9a227]"><span>{t("Reprise")}</span><span className="font-mono">- {euro(htOnly ? repriseHt : totalTradeIn)}</span></div>}
                <div className="flex justify-between"><span>{t(htOnly ? "Net à payer HT" : "Net à payer TTC")}</span><span className="font-mono">{euro(htOnly ? netHt : netTtc)}</span></div>
                <div className="flex justify-between"><span>{t("Acompte")}{htOnly ? " HT" : ""}</span><span className="font-mono">{euro(acompteValue * payK)}</span></div>
                <div className="flex justify-between"><span>{t("Solde à la livraison")}{htOnly ? " HT" : ""}</span><span className="font-mono" data-testid="recap-balance">{euro((netTtc - acompteValue) * payK)}</span></div>
                {countryCode === "FR" && financing && <div className="mt-1.5"><FinancingBlock financing={financing} priceTtc={totals.totalAfterTtc} solde={netTtc - acompteValue} testid="recap-financing" /></div>}
                <div className="text-xs text-muted-foreground mt-1">{t("Offre valable jusqu'au")} {fmtFR(offerValidUntil())}</div>
              </div>
            )}
          </div>
          <DialogFooter className="flex-col sm:flex-row sm:flex-wrap gap-2">
            <Button variant="outline" onClick={submitQuoteForm} disabled={saving} data-testid="form-submit" className="gap-2">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {t("Enregistrer")}
            </Button>
            {offlineSaved && (
              <Button variant="outline" onClick={() => openProvisionalPdf(provisionalQuote(buildPayload()))} data-testid="form-provisional-pdf" className="gap-2 border-amber-500 text-amber-700">
                <FileText className="w-4 h-4" /> {t("PDF provisoire")}
              </Button>
            )}
            <Button variant="outline" onClick={sendEmail} data-testid="form-send-email" className="gap-2">
              <Mail className="w-4 h-4" /> {t("Enregistrer + Envoyer")}
            </Button>
            {countryCode === "FR" && (
              <Button variant="outline" onClick={openFinancing} disabled={!client.name?.trim()} data-testid="form-open-financing" className="gap-2"
                title={client.name?.trim() ? "" : "Renseignez le nom du client"}>
                <Landmark className="w-4 h-4" /> {t("Voir les financements")}
              </Button>
            )}
            <Button onClick={openSignView} disabled={saving} data-testid="form-open-sign" className="gap-2">
              <Check className="w-4 h-4" /> {t("Voir le récapitulatif")}
            </Button>
            {colorMissing && <div className="text-xs text-destructive sm:basis-full sm:text-right" data-testid="color-required-msg">{t("Couleur à choisir avant de passer commande")}</div>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Comparateur de modèles */}
      <Dialog open={compareOpen} onOpenChange={setCompareOpen}>
        <DialogContent className="w-[96vw] max-w-[1100px] sm:max-w-[1100px]">
          <DialogHeader>
            <DialogTitle>{t("Comparer avec un autre modèle")}</DialogTitle>
            <DialogDescription>{t("Les mêmes options sélectionnées sont appliquées au modèle comparé (prix TTC).")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Label className="text-muted-foreground whitespace-nowrap">{t("Modèle à comparer :")}</Label>
              <Select value={compareId} onValueChange={setCompareId}>
                <SelectTrigger className="flex-1" data-testid="compare-select"><SelectValue placeholder={t("Choisir un modèle")} /></SelectTrigger>
                <SelectContent>
                  {catalog.models.filter((m) => m.id !== modelId).map((m) => (
                    <SelectItem key={m.id} value={m.id} data-testid={`compare-opt-${m.id}`}>{t(stripParen(m.name))}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {(() => {
              const vat = totals.vat || 0.2;
              const selList = Object.values(selected).filter((e) => e.qty > 0);
              const unitOf = (e) => {
                const cat = priceTtcOf(e.opt, vat);
                const effDisc = (e.disc || 0) > 0 ? e.disc : (promoMaps.optPub[e.opt.id] || 0);
                return (e.priceTtc != null && e.priceTtc !== "") ? Math.max(0, Number(e.priceTtc)) : cat * (1 - effDisc / 100);
              };
              const curModel = totals.modelAfterTtc;
              const curOpts = selList.reduce((s, e) => s + unitOf(e) * e.qty, 0);
              const spTot = specials.reduce((s, x) => s + spNet(x), 0);
              const cm = catalog.models.find((m) => m.id === compareId);
              if (!cm) return <p className="text-sm text-muted-foreground">{t("Sélectionnez un modèle pour voir la comparaison.")}</p>;
              const cmOpts = selList.reduce((s, e) => (compareAvail && !compareAvail.has(e.opt.id)) ? s : s + unitOf(e) * e.qty, 0);
              const extraOpts = compareExtras
                .map((id) => catalog.options.find((o) => o.id === id))
                .filter(Boolean)
                .filter((o) => !compareAvail || compareAvail.has(o.id));
              const cmExtras = extraOpts.reduce((s, o) => s + priceTtcOf(o, vat) * (1 - (promoMaps.optPub[o.id] || 0) / 100), 0);
              const cmModel = priceTtcOf(cm, vat) * (1 - promoModelPct(cm).pub / 100);
              const curTot = curModel + curOpts + spTot, cmpTot = cmModel + cmOpts + cmExtras + spTot;
              const excl = compareAvail ? selList.filter((e) => !compareAvail.has(e.opt.id)) : [];
              const Row = ({ label, a, b, strong }) => (
                <div className={`grid grid-cols-3 gap-2 py-1.5 ${strong ? "font-bold border-t border-border pt-2" : ""}`}>
                  <span className="text-muted-foreground">{label}</span>
                  <span className="text-right font-mono">{euro(a)}</span>
                  <span className="text-right font-mono">{euro(b)}</span>
                </div>
              );
              return (
                <div className="text-sm" data-testid="compare-result">
                  <div className="grid grid-cols-3 gap-2 pb-1 border-b border-border font-semibold">
                    <span />
                    <span className="text-right break-words leading-tight" title={model?.name}>{t(stripParen(model?.name))}</span>
                    <span className="text-right break-words leading-tight" title={cm.name}>{t(stripParen(cm.name))}</span>
                  </div>
                  <Row label={`${t("Prix du modèle TTC")}${activePromoIds.size ? ` (${t("promo incluse")})` : ""}`} a={curModel} b={cmModel} />
                  <Row label={`${t("Options TTC")} (${selList.length})`} a={curOpts} b={cmOpts} />
                  {spTot > 0 && <Row label={t("Options spéciales TTC")} a={spTot} b={spTot} />}
                  {extraOpts.length > 0 && (
                    <div className="grid grid-cols-3 gap-2 py-1.5">
                      <span className="text-muted-foreground">+ {t("Options ajoutées")}</span>
                      <span className="text-right font-mono text-muted-foreground">—</span>
                      <span className="text-right font-mono" data-testid="compare-extras">{euro(cmExtras)}</span>
                    </div>
                  )}
                  <Row label={t("Total TTC")} a={curTot} b={cmpTot} strong />
                  <div className="grid grid-cols-3 gap-2 pt-2 text-xs">
                    <span className="text-muted-foreground">{t("Écart TTC (comparé − actuel)")}</span>
                    <span />
                    <span className={`text-right font-mono font-semibold ${cmpTot > curTot ? "text-destructive" : "text-primary"}`} data-testid="compare-diff">
                      {(cmpTot - curTot >= 0 ? "+ " : "− ") + euro(Math.abs(cmpTot - curTot))}
                    </span>
                  </div>
                  {extraOpts.length > 0 && (
                    <p className="text-xs text-muted-foreground mt-3" data-testid="compare-extras-note">{t("Options ajoutées au modèle comparé :")} {extraOpts.map((o) => t(o.name)).join(", ")}</p>
                  )}
                  {excl.length > 0 && (
                    <p className="text-xs mt-3" data-testid="compare-excluded-note">
                      <span className="font-bold text-destructive uppercase">{t("Attention")}</span>
                      <span className="text-destructive"> — {t("options non disponibles sur")} {t(stripParen(cm.name))} ({t("non comptées")}) : {excl.map((e) => t(e.opt.name)).join(", ")}</span>
                    </p>
                  )}
                </div>
              );
            })()}
          </div>
        </DialogContent>
      </Dialog>
      <RibDialog defaultAttach={!!(savedQuote?.signature || "").trim() && acompteValue > 0} open={!!ribAsk} onOpenChange={(o) => { if (!o) setRibAsk(null); }} onConfirm={(p, b) => ribAsk?.(p, b)} />
      <SignView open={signOpen} onOpenChange={setSignOpen} quote={signQuote} confirmLabel={t("Signer et passer commande")} onConfirm={signAndOrder} busy={saving} onEditClient={signQuote?.stock_order ? undefined : () => { setSignOpen(false); setFormOpen(true); }} onEditQuote={() => setSignOpen(false)} onSendLink={signQuote?.stock_order ? undefined : sendSignLink} onCopyLink={signQuote?.stock_order ? undefined : copyLink} onPaperConfirm={signQuote?.stock_order ? undefined : paperOrder} />
      <PosterDialog open={posterOpen} onOpenChange={setPosterOpen} isAdmin={user?.role === "admin"} onCreate={rangeKey === "occasion" ? stockUsed : createPoster} />
      <PosterPreview pdf={posterPdf} onClose={() => setPosterPdf(null)} />
      <PosterPreview pdf={orderPdf} onClose={() => setOrderPdf(null)} testid="order-preview" printable title={t("Commande signée")} description={orderPdf?.paper ? t("Commande papier envoyée à vous et à contact@fautras.com (pas au client). Corinne la recevra une fois l'acompte reçu et le financement choisi.") : `${t("Voici le PDF que le client peut télécharger depuis l'e-mail envoyé à")} ${client.email || t("son adresse")}.`} />
      <Dialog open={!!photoPrompt} onOpenChange={(o) => !o && setPhotoPrompt(null)}>
        <DialogContent className="max-w-md" data-testid="photo-reminder">
          <DialogHeader>
            <DialogTitle>{t("Aucune photo de la reprise")}</DialogTitle>
            <DialogDescription>{t("Reprise")} {(photoPrompt?.i ?? 0) + 1} : {t("ajouter au moins une photo du véhicule repris est recommandé pour valider l'estimation.")}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" data-testid="photo-reminder-continue" onClick={() => { const c = photoPrompt.cont; photoAck.current = true; setPhotoPrompt(null); c(); }}>{t("Continuer sans photo")}</Button>
            <Button data-testid="photo-reminder-add" onClick={() => { const i = photoPrompt.i; setPhotoPrompt(null); goTo("__tradein__", `reprise-${i}-photo-add`, t("Ajoutez une photo du véhicule repris.")); }}>{t("Ajouter une photo")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
